// src/components/TackleBoxView.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { useRoutePortalTarget } from "./layout/RoutePortalHost";

import { View } from "../types";
import type {
  UserProfile,
  ToastType,
  TackleBoxItem,
  UserLicense,
  CosmeticSlot,
  UserCosmeticLoadout,
} from "../types";

import { AlertTriangleIcon, IdCardIcon } from "./icons";
import RoddoPageShell from "./layout/RoddoPageShell";
import RoddoPillTabs from "./RoddoPillTabs";
import MapAvatarMarker from "./avatar/MapAvatarMarker";
import {
  subscribeToEquippedGear,
  subscribeToGear,
  setEquippedGearSlot,
  setEquippedGearPatch,
} from "../services/gear";
import AddGearModal from "./AddGearModal";
import RoddoConfirmDialog from "./RoddoConfirmDialog";
import {
  getRoddoTheme,
  getRoddoUiTokens,
  resolveRoddoDarkMode,
} from "../constants/theme";
import { xpProgress as getXPProgress } from "../utils/xp";
import {
  cosmeticCatalog,
  getCosmeticById,
  getCosmeticSlotLabel,
  getPreviewShopCosmetics,
  getStarterCosmetics,
  resolveCosmeticLoadout,
} from "../constants/cosmetics";
import {
  ensureStarterCosmetics,
  equipAvatarCosmetic,
  unequipAvatarCosmeticSlot,
  purchaseAvatarCosmetic,
  subscribeToOwnedCosmetics,
  type OwnedCosmeticRecord,
} from "../services/cosmetics";
import { completeDailyTaskAction } from "../services/dailyRewards";
import {
  SEARCH_LURE_BUNDLES,
  SEARCH_LURE_FREE_REGEN_CAP,
  SEARCH_LURE_REGEN_INTERVAL_HOURS,
  getSearchLureWalletPreview,
  getSearchLureRefillLabel,
  purchaseSearchLureBundle,
  type SearchLureBundleId,
} from "../services/searchLures";
import { canOfferRewardedSearchLureAd, showRewardedSearchLureAd } from "../services/rewardedAds";
import { RODDO_ADMOB, REWARDED_SEARCH_LURE_AMOUNT, REWARDED_SEARCH_LURE_DAILY_CAP } from "../constants/adMob";

const roddoTactilePolishStyles = `
  [data-roddo-tactile] button,
  [data-roddo-tactile] [role="button"],
  [data-roddo-tactile] a,
  [data-roddo-tactile] label[for],
  [data-roddo-tactile] select,
  [data-roddo-tactile] input,
  [data-roddo-tactile] textarea {
    -webkit-tap-highlight-color: transparent;
  }

  [data-roddo-tactile] button,
  [data-roddo-tactile] [role="button"],
  [data-roddo-tactile] a {
    transform: translateZ(0);
    transition:
      transform 180ms cubic-bezier(0.22, 1, 0.36, 1),
      filter 180ms cubic-bezier(0.22, 1, 0.36, 1),
      box-shadow 220ms cubic-bezier(0.22, 1, 0.36, 1),
      opacity 180ms cubic-bezier(0.22, 1, 0.36, 1),
      background 220ms cubic-bezier(0.22, 1, 0.36, 1),
      border-color 220ms cubic-bezier(0.22, 1, 0.36, 1);
    will-change: transform, filter;
  }

  [data-roddo-tactile] button:hover,
  [data-roddo-tactile] [role="button"]:hover,
  [data-roddo-tactile] a:hover {
    transform: translateY(-1px) translateZ(0);
    filter: brightness(1.035) saturate(1.035);
  }

  [data-roddo-tactile] button:active,
  [data-roddo-tactile] [role="button"]:active,
  [data-roddo-tactile] a:active {
    transform: translateY(1px) scale(0.985) translateZ(0);
    filter: brightness(0.985) saturate(0.98);
  }

  [data-roddo-tactile] button:focus-visible,
  [data-roddo-tactile] [role="button"]:focus-visible,
  [data-roddo-tactile] a:focus-visible,
  [data-roddo-tactile] input:focus-visible,
  [data-roddo-tactile] textarea:focus-visible,
  [data-roddo-tactile] select:focus-visible {
    outline: none;
    box-shadow:
      0 0 0 3px color-mix(in srgb, #67D8E8 26%, transparent),
      0 0 18px color-mix(in srgb, #67D8E8 14%, transparent);
  }

  [data-roddo-tactile] .roddo-polish-card,
  [data-roddo-tactile] article {
    transition:
      transform 220ms cubic-bezier(0.22, 1, 0.36, 1),
      filter 220ms cubic-bezier(0.22, 1, 0.36, 1),
      box-shadow 260ms cubic-bezier(0.22, 1, 0.36, 1);
  }

  [data-roddo-tactile] .roddo-polish-card:hover,
  [data-roddo-tactile] article:hover {
    transform: translateY(-1px);
    filter: saturate(1.025) brightness(1.015);
  }

  [data-roddo-tactile] .roddo-polish-energy {
    position: relative;
    isolation: isolate;
    overflow: hidden;
  }

  [data-roddo-tactile] .roddo-polish-energy::before {
    content: none;
  }

  @media (prefers-reduced-motion: reduce) {
    [data-roddo-tactile] button,
    [data-roddo-tactile] [role="button"],
    [data-roddo-tactile] a,
    [data-roddo-tactile] .roddo-polish-card,
    [data-roddo-tactile] article {
      transition: none !important;
      transform: none !important;
      filter: none !important;
    }
  }
`;

interface TackleBoxViewProps {
  userProfile: UserProfile;
  navigateTo?: (view: View, state?: Record<string, any>) => void;
  addToast?: (message: string, type?: ToastType) => void;
  onOpenCatchHistory?: () => void;
  viewState?: { tab?: "tackle" | "stats" | "avatar" | "tips" };
  isDarkMode?: boolean;
}

type EquippedState = {
  rodId?: string | null;
  reelId?: string | null;
  lureOrBaitId?: string | null;
};

type EquippedSlot = keyof EquippedState;

const normalizeEquippedForUi = (equipped: any): EquippedState => ({
  rodId: equipped?.rodId ?? null,
  reelId: equipped?.reelId ?? null,
  lureOrBaitId: equipped?.lureOrBaitId ?? equipped?.lureId ?? null,
});

const applyPendingEquipped = (
  incoming: any,
  pending: Partial<Record<EquippedSlot, string | null>>,
): EquippedState => ({
  ...normalizeEquippedForUi(incoming),
  ...pending,
});

type TopTab = "tackle" | "stats" | "avatar";
type PickerSlot = null | "rod" | "reel" | "lureOrBait";
type AvatarShopFilter =
  | "all"
  | "head"
  | "outfit"
  | "gear"
  | "identity";

type PendingShellPurchase =
  | {
      kind: "cosmetic";
      cosmeticId: string;
      name: string;
      emoji: string;
      costShells: number;
    }
  | {
      kind: "search_lures";
      bundleId: SearchLureBundleId;
      name: string;
      quantity: number;
      costShells: number;
    };

type PurchaseSuccessState = {
  title: string;
  description: string;
  icon: string;
  shellBalance: number;
  searchLureBalance?: number;
};

const cosmeticLoadoutKey = (loadout: any) => JSON.stringify(loadout || {});

function getLoadoutItems(loadout: UserCosmeticLoadout | null | undefined) {
  const resolved = resolveCosmeticLoadout(loadout);
  return Object.values(resolved)
    .map((id) => getCosmeticById(id))
    .filter(Boolean) as NonNullable<ReturnType<typeof getCosmeticById>>[];
}

const isBlobUrl = (url: any) =>
  typeof url === "string" && url.startsWith("blob:");

function formatDateShort(value: any): string {
  if (!value) return "—";
  const d =
    value?.toDate?.() instanceof Date
      ? value.toDate()
      : value instanceof Date
        ? value
        : new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString();
}

function toDateSafe(value: any): Date | null {
  if (!value) return null;
  const d =
    value?.toDate?.() instanceof Date
      ? value.toDate()
      : value instanceof Date
        ? value
        : new Date(value);
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null;
  return d;
}

const US_STATE_NAME_TO_CODE: Record<string, string> = {
  alabama: "AL",
  alaska: "AK",
  arizona: "AZ",
  arkansas: "AR",
  california: "CA",
  colorado: "CO",
  connecticut: "CT",
  delaware: "DE",
  florida: "FL",
  georgia: "GA",
  hawaii: "HI",
  idaho: "ID",
  illinois: "IL",
  indiana: "IN",
  iowa: "IA",
  kansas: "KS",
  kentucky: "KY",
  louisiana: "LA",
  maine: "ME",
  maryland: "MD",
  massachusetts: "MA",
  michigan: "MI",
  minnesota: "MN",
  mississippi: "MS",
  missouri: "MO",
  montana: "MT",
  nebraska: "NE",
  nevada: "NV",
  "new hampshire": "NH",
  "new jersey": "NJ",
  "new mexico": "NM",
  "new york": "NY",
  "north carolina": "NC",
  "north dakota": "ND",
  ohio: "OH",
  oklahoma: "OK",
  oregon: "OR",
  pennsylvania: "PA",
  "rhode island": "RI",
  "south carolina": "SC",
  "south dakota": "SD",
  tennessee: "TN",
  texas: "TX",
  utah: "UT",
  vermont: "VT",
  virginia: "VA",
  washington: "WA",
  "west virginia": "WV",
  wisconsin: "WI",
  wyoming: "WY",
  "district of columbia": "DC",
  "washington, dc": "DC",
  "d.c.": "DC",
};

type LicenseCountryOption = { code: string; name: string };
type LicenseRegionOption = { code: string; name: string };

const LICENSE_COUNTRIES: LicenseCountryOption[] = [
  { code: "AF", name: "Afghanistan" },
  { code: "AL", name: "Albania" },
  { code: "DZ", name: "Algeria" },
  { code: "AS", name: "American Samoa" },
  { code: "AD", name: "Andorra" },
  { code: "AO", name: "Angola" },
  { code: "AI", name: "Anguilla" },
  { code: "AQ", name: "Antarctica" },
  { code: "AG", name: "Antigua and Barbuda" },
  { code: "AR", name: "Argentina" },
  { code: "AM", name: "Armenia" },
  { code: "AW", name: "Aruba" },
  { code: "AU", name: "Australia" },
  { code: "AT", name: "Austria" },
  { code: "AZ", name: "Azerbaijan" },
  { code: "BS", name: "Bahamas" },
  { code: "BH", name: "Bahrain" },
  { code: "BD", name: "Bangladesh" },
  { code: "BB", name: "Barbados" },
  { code: "BY", name: "Belarus" },
  { code: "BE", name: "Belgium" },
  { code: "BZ", name: "Belize" },
  { code: "BJ", name: "Benin" },
  { code: "BM", name: "Bermuda" },
  { code: "BT", name: "Bhutan" },
  { code: "BO", name: "Bolivia" },
  { code: "BQ", name: "Bonaire, Sint Eustatius and Saba" },
  { code: "BA", name: "Bosnia and Herzegovina" },
  { code: "BW", name: "Botswana" },
  { code: "BV", name: "Bouvet Island" },
  { code: "BR", name: "Brazil" },
  { code: "IO", name: "British Indian Ocean Territory" },
  { code: "BN", name: "Brunei Darussalam" },
  { code: "BG", name: "Bulgaria" },
  { code: "BF", name: "Burkina Faso" },
  { code: "BI", name: "Burundi" },
  { code: "CV", name: "Cabo Verde" },
  { code: "KH", name: "Cambodia" },
  { code: "CM", name: "Cameroon" },
  { code: "CA", name: "Canada" },
  { code: "KY", name: "Cayman Islands" },
  { code: "CF", name: "Central African Republic" },
  { code: "TD", name: "Chad" },
  { code: "CL", name: "Chile" },
  { code: "CN", name: "China" },
  { code: "CX", name: "Christmas Island" },
  { code: "CC", name: "Cocos (Keeling) Islands" },
  { code: "CO", name: "Colombia" },
  { code: "KM", name: "Comoros" },
  { code: "CG", name: "Congo" },
  { code: "CD", name: "Congo, The Democratic Republic of the" },
  { code: "CK", name: "Cook Islands" },
  { code: "CR", name: "Costa Rica" },
  { code: "HR", name: "Croatia" },
  { code: "CU", name: "Cuba" },
  { code: "CW", name: "Cura\u00e7ao" },
  { code: "CY", name: "Cyprus" },
  { code: "CZ", name: "Czechia" },
  { code: "CI", name: "C\u00f4te d'Ivoire" },
  { code: "DK", name: "Denmark" },
  { code: "DJ", name: "Djibouti" },
  { code: "DM", name: "Dominica" },
  { code: "DO", name: "Dominican Republic" },
  { code: "EC", name: "Ecuador" },
  { code: "EG", name: "Egypt" },
  { code: "SV", name: "El Salvador" },
  { code: "GQ", name: "Equatorial Guinea" },
  { code: "ER", name: "Eritrea" },
  { code: "EE", name: "Estonia" },
  { code: "SZ", name: "Eswatini" },
  { code: "ET", name: "Ethiopia" },
  { code: "FK", name: "Falkland Islands (Malvinas)" },
  { code: "FO", name: "Faroe Islands" },
  { code: "FJ", name: "Fiji" },
  { code: "FI", name: "Finland" },
  { code: "FR", name: "France" },
  { code: "GF", name: "French Guiana" },
  { code: "PF", name: "French Polynesia" },
  { code: "TF", name: "French Southern Territories" },
  { code: "GA", name: "Gabon" },
  { code: "GM", name: "Gambia" },
  { code: "GE", name: "Georgia" },
  { code: "DE", name: "Germany" },
  { code: "GH", name: "Ghana" },
  { code: "GI", name: "Gibraltar" },
  { code: "GR", name: "Greece" },
  { code: "GL", name: "Greenland" },
  { code: "GD", name: "Grenada" },
  { code: "GP", name: "Guadeloupe" },
  { code: "GU", name: "Guam" },
  { code: "GT", name: "Guatemala" },
  { code: "GG", name: "Guernsey" },
  { code: "GN", name: "Guinea" },
  { code: "GW", name: "Guinea-Bissau" },
  { code: "GY", name: "Guyana" },
  { code: "HT", name: "Haiti" },
  { code: "HM", name: "Heard Island and McDonald Islands" },
  { code: "VA", name: "Holy See (Vatican City State)" },
  { code: "HN", name: "Honduras" },
  { code: "HK", name: "Hong Kong" },
  { code: "HU", name: "Hungary" },
  { code: "IS", name: "Iceland" },
  { code: "IN", name: "India" },
  { code: "ID", name: "Indonesia" },
  { code: "IR", name: "Iran" },
  { code: "IQ", name: "Iraq" },
  { code: "IE", name: "Ireland" },
  { code: "IM", name: "Isle of Man" },
  { code: "IL", name: "Israel" },
  { code: "IT", name: "Italy" },
  { code: "JM", name: "Jamaica" },
  { code: "JP", name: "Japan" },
  { code: "JE", name: "Jersey" },
  { code: "JO", name: "Jordan" },
  { code: "KZ", name: "Kazakhstan" },
  { code: "KE", name: "Kenya" },
  { code: "KI", name: "Kiribati" },
  { code: "KW", name: "Kuwait" },
  { code: "KG", name: "Kyrgyzstan" },
  { code: "LA", name: "Laos" },
  { code: "LV", name: "Latvia" },
  { code: "LB", name: "Lebanon" },
  { code: "LS", name: "Lesotho" },
  { code: "LR", name: "Liberia" },
  { code: "LY", name: "Libya" },
  { code: "LI", name: "Liechtenstein" },
  { code: "LT", name: "Lithuania" },
  { code: "LU", name: "Luxembourg" },
  { code: "MO", name: "Macao" },
  { code: "MG", name: "Madagascar" },
  { code: "MW", name: "Malawi" },
  { code: "MY", name: "Malaysia" },
  { code: "MV", name: "Maldives" },
  { code: "ML", name: "Mali" },
  { code: "MT", name: "Malta" },
  { code: "MH", name: "Marshall Islands" },
  { code: "MQ", name: "Martinique" },
  { code: "MR", name: "Mauritania" },
  { code: "MU", name: "Mauritius" },
  { code: "YT", name: "Mayotte" },
  { code: "MX", name: "Mexico" },
  { code: "FM", name: "Micronesia, Federated States of" },
  { code: "MD", name: "Moldova" },
  { code: "MC", name: "Monaco" },
  { code: "MN", name: "Mongolia" },
  { code: "ME", name: "Montenegro" },
  { code: "MS", name: "Montserrat" },
  { code: "MA", name: "Morocco" },
  { code: "MZ", name: "Mozambique" },
  { code: "MM", name: "Myanmar" },
  { code: "NA", name: "Namibia" },
  { code: "NR", name: "Nauru" },
  { code: "NP", name: "Nepal" },
  { code: "NL", name: "Netherlands" },
  { code: "NC", name: "New Caledonia" },
  { code: "NZ", name: "New Zealand" },
  { code: "NI", name: "Nicaragua" },
  { code: "NE", name: "Niger" },
  { code: "NG", name: "Nigeria" },
  { code: "NU", name: "Niue" },
  { code: "NF", name: "Norfolk Island" },
  { code: "KP", name: "North Korea" },
  { code: "MK", name: "North Macedonia" },
  { code: "MP", name: "Northern Mariana Islands" },
  { code: "NO", name: "Norway" },
  { code: "OM", name: "Oman" },
  { code: "PK", name: "Pakistan" },
  { code: "PW", name: "Palau" },
  { code: "PS", name: "Palestine, State of" },
  { code: "PA", name: "Panama" },
  { code: "PG", name: "Papua New Guinea" },
  { code: "PY", name: "Paraguay" },
  { code: "PE", name: "Peru" },
  { code: "PH", name: "Philippines" },
  { code: "PN", name: "Pitcairn" },
  { code: "PL", name: "Poland" },
  { code: "PT", name: "Portugal" },
  { code: "PR", name: "Puerto Rico" },
  { code: "QA", name: "Qatar" },
  { code: "RO", name: "Romania" },
  { code: "RU", name: "Russian Federation" },
  { code: "RW", name: "Rwanda" },
  { code: "RE", name: "R\u00e9union" },
  { code: "BL", name: "Saint Barth\u00e9lemy" },
  { code: "SH", name: "Saint Helena, Ascension and Tristan da Cunha" },
  { code: "KN", name: "Saint Kitts and Nevis" },
  { code: "LC", name: "Saint Lucia" },
  { code: "MF", name: "Saint Martin (French part)" },
  { code: "PM", name: "Saint Pierre and Miquelon" },
  { code: "VC", name: "Saint Vincent and the Grenadines" },
  { code: "WS", name: "Samoa" },
  { code: "SM", name: "San Marino" },
  { code: "ST", name: "Sao Tome and Principe" },
  { code: "SA", name: "Saudi Arabia" },
  { code: "SN", name: "Senegal" },
  { code: "RS", name: "Serbia" },
  { code: "SC", name: "Seychelles" },
  { code: "SL", name: "Sierra Leone" },
  { code: "SG", name: "Singapore" },
  { code: "SX", name: "Sint Maarten (Dutch part)" },
  { code: "SK", name: "Slovakia" },
  { code: "SI", name: "Slovenia" },
  { code: "SB", name: "Solomon Islands" },
  { code: "SO", name: "Somalia" },
  { code: "ZA", name: "South Africa" },
  { code: "GS", name: "South Georgia and the South Sandwich Islands" },
  { code: "KR", name: "South Korea" },
  { code: "SS", name: "South Sudan" },
  { code: "ES", name: "Spain" },
  { code: "LK", name: "Sri Lanka" },
  { code: "SD", name: "Sudan" },
  { code: "SR", name: "Suriname" },
  { code: "SJ", name: "Svalbard and Jan Mayen" },
  { code: "SE", name: "Sweden" },
  { code: "CH", name: "Switzerland" },
  { code: "SY", name: "Syria" },
  { code: "TW", name: "Taiwan" },
  { code: "TJ", name: "Tajikistan" },
  { code: "TZ", name: "Tanzania" },
  { code: "TH", name: "Thailand" },
  { code: "TL", name: "Timor-Leste" },
  { code: "TG", name: "Togo" },
  { code: "TK", name: "Tokelau" },
  { code: "TO", name: "Tonga" },
  { code: "TT", name: "Trinidad and Tobago" },
  { code: "TN", name: "Tunisia" },
  { code: "TM", name: "Turkmenistan" },
  { code: "TC", name: "Turks and Caicos Islands" },
  { code: "TV", name: "Tuvalu" },
  { code: "TR", name: "T\u00fcrkiye" },
  { code: "UG", name: "Uganda" },
  { code: "UA", name: "Ukraine" },
  { code: "AE", name: "United Arab Emirates" },
  { code: "GB", name: "United Kingdom" },
  { code: "US", name: "United States" },
  { code: "UM", name: "United States Minor Outlying Islands" },
  { code: "UY", name: "Uruguay" },
  { code: "UZ", name: "Uzbekistan" },
  { code: "VU", name: "Vanuatu" },
  { code: "VE", name: "Venezuela" },
  { code: "VN", name: "Vietnam" },
  { code: "VG", name: "Virgin Islands, British" },
  { code: "VI", name: "Virgin Islands, U.S." },
  { code: "WF", name: "Wallis and Futuna" },
  { code: "EH", name: "Western Sahara" },
  { code: "YE", name: "Yemen" },
  { code: "ZM", name: "Zambia" },
  { code: "ZW", name: "Zimbabwe" },
  { code: "AX", name: "\u00c5land Islands" },
];

const LICENSE_COUNTRY_NAME_TO_CODE = new Map(
  LICENSE_COUNTRIES.flatMap((country) => [
    [country.name.toLowerCase(), country.code],
    [country.code.toLowerCase(), country.code],
  ] as Array<[string, string]>),
);

const LICENSE_REGIONS: Record<string, LicenseRegionOption[]> = {
  US: [
    { code: "AL", name: "Alabama" },
    { code: "AK", name: "Alaska" },
    { code: "AS", name: "American Samoa" },
    { code: "AZ", name: "Arizona" },
    { code: "AR", name: "Arkansas" },
    { code: "CA", name: "California" },
    { code: "CO", name: "Colorado" },
    { code: "CT", name: "Connecticut" },
    { code: "DE", name: "Delaware" },
    { code: "DC", name: "District of Columbia" },
    { code: "FL", name: "Florida" },
    { code: "GA", name: "Georgia" },
    { code: "GU", name: "Guam" },
    { code: "HI", name: "Hawaii" },
    { code: "ID", name: "Idaho" },
    { code: "IL", name: "Illinois" },
    { code: "IN", name: "Indiana" },
    { code: "IA", name: "Iowa" },
    { code: "KS", name: "Kansas" },
    { code: "KY", name: "Kentucky" },
    { code: "LA", name: "Louisiana" },
    { code: "ME", name: "Maine" },
    { code: "MD", name: "Maryland" },
    { code: "MA", name: "Massachusetts" },
    { code: "MI", name: "Michigan" },
    { code: "MN", name: "Minnesota" },
    { code: "MS", name: "Mississippi" },
    { code: "MO", name: "Missouri" },
    { code: "MT", name: "Montana" },
    { code: "NE", name: "Nebraska" },
    { code: "NV", name: "Nevada" },
    { code: "NH", name: "New Hampshire" },
    { code: "NJ", name: "New Jersey" },
    { code: "NM", name: "New Mexico" },
    { code: "NY", name: "New York" },
    { code: "NC", name: "North Carolina" },
    { code: "ND", name: "North Dakota" },
    { code: "MP", name: "Northern Mariana Islands" },
    { code: "OH", name: "Ohio" },
    { code: "OK", name: "Oklahoma" },
    { code: "OR", name: "Oregon" },
    { code: "PA", name: "Pennsylvania" },
    { code: "PR", name: "Puerto Rico" },
    { code: "RI", name: "Rhode Island" },
    { code: "SC", name: "South Carolina" },
    { code: "SD", name: "South Dakota" },
    { code: "TN", name: "Tennessee" },
    { code: "TX", name: "Texas" },
    { code: "UM", name: "United States Minor Outlying Islands" },
    { code: "UT", name: "Utah" },
    { code: "VT", name: "Vermont" },
    { code: "VI", name: "Virgin Islands, U.S." },
    { code: "VA", name: "Virginia" },
    { code: "WA", name: "Washington" },
    { code: "WV", name: "West Virginia" },
    { code: "WI", name: "Wisconsin" },
    { code: "WY", name: "Wyoming" },
  ],
  CA: [
    { code: "AB", name: "Alberta" },
    { code: "BC", name: "British Columbia" },
    { code: "MB", name: "Manitoba" },
    { code: "NB", name: "New Brunswick" },
    { code: "NL", name: "Newfoundland and Labrador" },
    { code: "NT", name: "Northwest Territories" },
    { code: "NS", name: "Nova Scotia" },
    { code: "NU", name: "Nunavut" },
    { code: "ON", name: "Ontario" },
    { code: "PE", name: "Prince Edward Island" },
    { code: "QC", name: "Quebec" },
    { code: "SK", name: "Saskatchewan" },
    { code: "YT", name: "Yukon" },
  ],
  AU: [
    { code: "ACT", name: "Australian Capital Territory" },
    { code: "NSW", name: "New South Wales" },
    { code: "NT", name: "Northern Territory" },
    { code: "QLD", name: "Queensland" },
    { code: "SA", name: "South Australia" },
    { code: "TAS", name: "Tasmania" },
    { code: "VIC", name: "Victoria" },
    { code: "WA", name: "Western Australia" },
  ],
  GB: [
    { code: "ABE", name: "Aberdeen City" },
    { code: "ABD", name: "Aberdeenshire" },
    { code: "ANS", name: "Angus" },
    { code: "ANN", name: "Antrim and Newtownabbey" },
    { code: "AND", name: "Ards and North Down" },
    { code: "AGB", name: "Argyll and Bute" },
    { code: "ABC", name: "Armagh City, Banbridge and Craigavon" },
    { code: "BDG", name: "Barking and Dagenham" },
    { code: "BNE", name: "Barnet" },
    { code: "BNS", name: "Barnsley" },
    { code: "BAS", name: "Bath and North East Somerset" },
    { code: "BDF", name: "Bedford" },
    { code: "BFS", name: "Belfast City" },
    { code: "BEX", name: "Bexley" },
    { code: "BIR", name: "Birmingham" },
    { code: "BBD", name: "Blackburn with Darwen" },
    { code: "BPL", name: "Blackpool" },
    { code: "BGW", name: "Blaenau Gwent" },
    { code: "BOL", name: "Bolton" },
    { code: "BCP", name: "Bournemouth, Christchurch and Poole" },
    { code: "BRC", name: "Bracknell Forest" },
    { code: "BRD", name: "Bradford" },
    { code: "BEN", name: "Brent" },
    { code: "BGE", name: "Bridgend [Pen-y-bont ar Ogwr GB-POG]" },
    { code: "BNH", name: "Brighton and Hove" },
    { code: "BST", name: "Bristol, City of" },
    { code: "BRY", name: "Bromley" },
    { code: "BKM", name: "Buckinghamshire" },
    { code: "BUR", name: "Bury" },
    { code: "CAY", name: "Caerphilly [Caerffili GB-CAF]" },
    { code: "CLD", name: "Calderdale" },
    { code: "CAM", name: "Cambridgeshire" },
    { code: "CMD", name: "Camden" },
    { code: "CRF", name: "Cardiff [Caerdydd GB-CRD]" },
    { code: "CMN", name: "Carmarthenshire [Sir Gaerfyrddin GB-GFY]" },
    { code: "CCG", name: "Causeway Coast and Glens" },
    { code: "CBF", name: "Central Bedfordshire" },
    { code: "CGN", name: "Ceredigion [Sir Ceredigion]" },
    { code: "CHE", name: "Cheshire East" },
    { code: "CHW", name: "Cheshire West and Chester" },
    { code: "CLK", name: "Clackmannanshire" },
    { code: "CWY", name: "Conwy" },
    { code: "CON", name: "Cornwall" },
    { code: "COV", name: "Coventry" },
    { code: "CRY", name: "Croydon" },
    { code: "CMA", name: "Cumbria" },
    { code: "DAL", name: "Darlington" },
    { code: "DEN", name: "Denbighshire [Sir Ddinbych GB-DDB]" },
    { code: "DER", name: "Derby" },
    { code: "DBY", name: "Derbyshire" },
    { code: "DRS", name: "Derry and Strabane" },
    { code: "DEV", name: "Devon" },
    { code: "DNC", name: "Doncaster" },
    { code: "DOR", name: "Dorset" },
    { code: "DUD", name: "Dudley" },
    { code: "DGY", name: "Dumfries and Galloway" },
    { code: "DND", name: "Dundee City" },
    { code: "DUR", name: "Durham, County" },
    { code: "EAL", name: "Ealing" },
    { code: "EAY", name: "East Ayrshire" },
    { code: "EDU", name: "East Dunbartonshire" },
    { code: "ELN", name: "East Lothian" },
    { code: "ERW", name: "East Renfrewshire" },
    { code: "ERY", name: "East Riding of Yorkshire" },
    { code: "ESX", name: "East Sussex" },
    { code: "EDH", name: "Edinburgh, City of" },
    { code: "ELS", name: "Eilean Siar" },
    { code: "ENF", name: "Enfield" },
    { code: "ENG", name: "England" },
    { code: "ESS", name: "Essex" },
    { code: "FAL", name: "Falkirk" },
    { code: "FMO", name: "Fermanagh and Omagh" },
    { code: "FIF", name: "Fife" },
    { code: "FLN", name: "Flintshire [Sir y Fflint GB-FFL]" },
    { code: "GAT", name: "Gateshead" },
    { code: "GLG", name: "Glasgow City" },
    { code: "GLS", name: "Gloucestershire" },
    { code: "GRE", name: "Greenwich" },
    { code: "GWN", name: "Gwynedd" },
    { code: "HCK", name: "Hackney" },
    { code: "HAL", name: "Halton" },
    { code: "HMF", name: "Hammersmith and Fulham" },
    { code: "HAM", name: "Hampshire" },
    { code: "HRY", name: "Haringey" },
    { code: "HRW", name: "Harrow" },
    { code: "HPL", name: "Hartlepool" },
    { code: "HAV", name: "Havering" },
    { code: "HEF", name: "Herefordshire" },
    { code: "HRT", name: "Hertfordshire" },
    { code: "HLD", name: "Highland" },
    { code: "HIL", name: "Hillingdon" },
    { code: "HNS", name: "Hounslow" },
    { code: "IVC", name: "Inverclyde" },
    { code: "AGY", name: "Isle of Anglesey [Sir Ynys M\u00f4n GB-YNM]" },
    { code: "IOW", name: "Isle of Wight" },
    { code: "IOS", name: "Isles of Scilly" },
    { code: "ISL", name: "Islington" },
    { code: "KEC", name: "Kensington and Chelsea" },
    { code: "KEN", name: "Kent" },
    { code: "KHL", name: "Kingston upon Hull" },
    { code: "KTT", name: "Kingston upon Thames" },
    { code: "KIR", name: "Kirklees" },
    { code: "KWL", name: "Knowsley" },
    { code: "LBH", name: "Lambeth" },
    { code: "LAN", name: "Lancashire" },
    { code: "LDS", name: "Leeds" },
    { code: "LCE", name: "Leicester" },
    { code: "LEC", name: "Leicestershire" },
    { code: "LEW", name: "Lewisham" },
    { code: "LIN", name: "Lincolnshire" },
    { code: "LBC", name: "Lisburn and Castlereagh" },
    { code: "LIV", name: "Liverpool" },
    { code: "LND", name: "London, City of" },
    { code: "LUT", name: "Luton" },
    { code: "MAN", name: "Manchester" },
    { code: "MDW", name: "Medway" },
    { code: "MTY", name: "Merthyr Tydfil [Merthyr Tudful GB-MTU]" },
    { code: "MRT", name: "Merton" },
    { code: "MEA", name: "Mid and East Antrim" },
    { code: "MUL", name: "Mid-Ulster" },
    { code: "MDB", name: "Middlesbrough" },
    { code: "MLN", name: "Midlothian" },
    { code: "MIK", name: "Milton Keynes" },
    { code: "MON", name: "Monmouthshire [Sir Fynwy GB-FYN]" },
    { code: "MRY", name: "Moray" },
    { code: "NTL", name: "Neath Port Talbot [Castell-nedd Port Talbot GB-CTL]" },
    { code: "NET", name: "Newcastle upon Tyne" },
    { code: "NWM", name: "Newham" },
    { code: "NWP", name: "Newport [Casnewydd GB-CNW]" },
    { code: "NMD", name: "Newry, Mourne and Down" },
    { code: "NFK", name: "Norfolk" },
    { code: "NAY", name: "North Ayrshire" },
    { code: "NEL", name: "North East Lincolnshire" },
    { code: "NLK", name: "North Lanarkshire" },
    { code: "NLN", name: "North Lincolnshire" },
    { code: "NNH", name: "North Northamptonshire" },
    { code: "NSM", name: "North Somerset" },
    { code: "NTY", name: "North Tyneside" },
    { code: "NYK", name: "North Yorkshire" },
    { code: "NIR", name: "Northern Ireland" },
    { code: "NBL", name: "Northumberland" },
    { code: "NGM", name: "Nottingham" },
    { code: "NTT", name: "Nottinghamshire" },
    { code: "OLD", name: "Oldham" },
    { code: "ORK", name: "Orkney Islands" },
    { code: "OXF", name: "Oxfordshire" },
    { code: "PEM", name: "Pembrokeshire [Sir Benfro GB-BNF]" },
    { code: "PKN", name: "Perth and Kinross" },
    { code: "PTE", name: "Peterborough" },
    { code: "PLY", name: "Plymouth" },
    { code: "POR", name: "Portsmouth" },
    { code: "POW", name: "Powys" },
    { code: "RDG", name: "Reading" },
    { code: "RDB", name: "Redbridge" },
    { code: "RCC", name: "Redcar and Cleveland" },
    { code: "RFW", name: "Renfrewshire" },
    { code: "RCT", name: "Rhondda Cynon Taff [Rhondda CynonTaf]" },
    { code: "RIC", name: "Richmond upon Thames" },
    { code: "RCH", name: "Rochdale" },
    { code: "ROT", name: "Rotherham" },
    { code: "RUT", name: "Rutland" },
    { code: "SLF", name: "Salford" },
    { code: "SAW", name: "Sandwell" },
    { code: "SCT", name: "Scotland" },
    { code: "SCB", name: "Scottish Borders" },
    { code: "SFT", name: "Sefton" },
    { code: "SHF", name: "Sheffield" },
    { code: "ZET", name: "Shetland Islands" },
    { code: "SHR", name: "Shropshire" },
    { code: "SLG", name: "Slough" },
    { code: "SOL", name: "Solihull" },
    { code: "SOM", name: "Somerset" },
    { code: "SAY", name: "South Ayrshire" },
    { code: "SGC", name: "South Gloucestershire" },
    { code: "SLK", name: "South Lanarkshire" },
    { code: "STY", name: "South Tyneside" },
    { code: "STH", name: "Southampton" },
    { code: "SOS", name: "Southend-on-Sea" },
    { code: "SWK", name: "Southwark" },
    { code: "SHN", name: "St. Helens" },
    { code: "STS", name: "Staffordshire" },
    { code: "STG", name: "Stirling" },
    { code: "SKP", name: "Stockport" },
    { code: "STT", name: "Stockton-on-Tees" },
    { code: "STE", name: "Stoke-on-Trent" },
    { code: "SFK", name: "Suffolk" },
    { code: "SND", name: "Sunderland" },
    { code: "SRY", name: "Surrey" },
    { code: "STN", name: "Sutton" },
    { code: "SWA", name: "Swansea [Abertawe GB-ATA]" },
    { code: "SWD", name: "Swindon" },
    { code: "TAM", name: "Tameside" },
    { code: "TFW", name: "Telford and Wrekin" },
    { code: "THR", name: "Thurrock" },
    { code: "TOB", name: "Torbay" },
    { code: "TOF", name: "Torfaen [Tor-faen]" },
    { code: "TWH", name: "Tower Hamlets" },
    { code: "TRF", name: "Trafford" },
    { code: "VGL", name: "Vale of Glamorgan, The [Bro Morgannwg GB-BMG]" },
    { code: "WKF", name: "Wakefield" },
    { code: "WLS", name: "Wales [Cymru GB-CYM]" },
    { code: "WLL", name: "Walsall" },
    { code: "WFT", name: "Waltham Forest" },
    { code: "WND", name: "Wandsworth" },
    { code: "WRT", name: "Warrington" },
    { code: "WAR", name: "Warwickshire" },
    { code: "WBK", name: "West Berkshire" },
    { code: "WDU", name: "West Dunbartonshire" },
    { code: "WLN", name: "West Lothian" },
    { code: "WNH", name: "West Northamptonshire" },
    { code: "WSX", name: "West Sussex" },
    { code: "WSM", name: "Westminster" },
    { code: "WGN", name: "Wigan" },
    { code: "WIL", name: "Wiltshire" },
    { code: "WNM", name: "Windsor and Maidenhead" },
    { code: "WRL", name: "Wirral" },
    { code: "WOK", name: "Wokingham" },
    { code: "WLV", name: "Wolverhampton" },
    { code: "WOR", name: "Worcestershire" },
    { code: "WRX", name: "Wrexham [Wrecsam GB-WRC]" },
    { code: "YOR", name: "York" },
  ],
};

const VERIFIED_LICENSE_AUTHORITY_URLS: Record<string, string> = {
  "US-CA": "https://wildlife.ca.gov/Licensing",
  "CA-ON": "https://www.ontario.ca/page/fishing-licence-ontario-and-canadian-residents",
  "CA-QC": "https://www.quebec.ca/en/tourism-recreation-sport/sporting-and-outdoor-activities/sport-fishing/fishing-licence",
  "CA-BC": "https://www2.gov.bc.ca/gov/content/sports-culture/recreation/fishing-hunting/fishing/recreational-freshwater-fishing-licence",
  "CA": "https://www.dfo-mpo.gc.ca/fisheries-peches/recreational-recreative/index-eng.html",
  "AU": "https://www.agriculture.gov.au/fisheries/recreational",
  "GB-ENG": "https://www.gov.uk/fishing-licences/buy-a-fishing-licence",
  "GB-WLS": "https://www.gov.uk/fishing-licences/buy-a-fishing-licence",
  "GB-SCT": "https://www.gov.uk/fishing-licences/fishing-in-scotland-and-northern-ireland",
  "GB-NIR": "https://www.gov.uk/fishing-licences/fishing-in-scotland-and-northern-ireland",
  "GB": "https://www.gov.uk/fishing-licences",
  "NZ": "https://www.mpi.govt.nz/fishing-aquaculture/recreational-fishing/fishing-rules",
  "PH": "https://www.bfar.da.gov.ph/capture-fisheries-licensing/",
};

const countryNameForCode = (code: string) =>
  LICENSE_COUNTRIES.find((country) => country.code === String(code || "").toUpperCase())?.name || String(code || "").toUpperCase();

const regionNameForCode = (countryCode: string, regionCode: string) =>
  (LICENSE_REGIONS[String(countryCode || "").toUpperCase()] || []).find(
    (region) => region.code === String(regionCode || "").toUpperCase(),
  )?.name || String(regionCode || "").toUpperCase();

function normalizeCountryToCode(input?: string | null): string | null {
  if (!input) return null;
  const raw = String(input).trim();
  if (!raw) return null;
  const direct = LICENSE_COUNTRY_NAME_TO_CODE.get(raw.toLowerCase());
  if (direct) return direct;
  const upper = raw.toUpperCase();
  return /^[A-Z]{2}$/.test(upper) && LICENSE_COUNTRIES.some((country) => country.code === upper) ? upper : null;
}

function detectBrowserCountryCode(): string | null {
  if (typeof navigator === "undefined") return null;
  const locales = [navigator.language, ...(Array.isArray(navigator.languages) ? navigator.languages : [])].filter(Boolean);
  for (const locale of locales) {
    try {
      const region = new Intl.Locale(String(locale)).region;
      const code = normalizeCountryToCode(region);
      if (code) return code;
    } catch {}
  }
  return null;
}

function normalizeLicenseRegion(countryCode: string, input?: string | null): string {
  const country = String(countryCode || "").toUpperCase();
  const raw = String(input || "").trim();
  if (!raw) return "";

  if (country === "US") return normalizeStateToCode(raw) || raw.toUpperCase();

  const options = LICENSE_REGIONS[country] || [];
  const upper = raw.toUpperCase();
  const direct = options.find((option) => option.code === upper);
  if (direct) return direct.code;
  const byName = options.find((option) => option.name.toLowerCase() === raw.toLowerCase());
  return byName?.code || raw;
}

function buildOfficialLicenseUrl(countryCode: string, regionCode?: string | null): string {
  const country = String(countryCode || "").toUpperCase();
  const region = String(regionCode || "").toUpperCase();
  const exactKey = country && region ? `${country}-${region}` : "";
  if (exactKey && VERIFIED_LICENSE_AUTHORITY_URLS[exactKey]) return VERIFIED_LICENSE_AUTHORITY_URLS[exactKey];
  if (VERIFIED_LICENSE_AUTHORITY_URLS[country]) return VERIFIED_LICENSE_AUTHORITY_URLS[country];

  const countryName = countryNameForCode(country);
  const regionName = region ? regionNameForCode(country, region) : "";
  const q = encodeURIComponent(
    [countryName, regionName, "official government recreational fishing licence permit regulations"].filter(Boolean).join(" "),
  );
  return `https://www.google.com/search?q=${q}`;
}

function normalizeAnalyticsText(value: any): string {
  return String(value ?? "").trim();
}

function pickFirstValue(source: any, keys: string[]): any {
  if (!source || typeof source !== "object") return undefined;

  const pools = [
    source,
    source?.raw,
    source?.catchData,
    source?.raw?.catchData,
    source?.communitySignal,
    source?.raw?.communitySignal,
  ];

  for (const pool of pools) {
    if (!pool || typeof pool !== "object") continue;
    for (const key of keys) {
      const value = pool?.[key];
      if (value !== undefined && value !== null && value !== "") return value;
    }
  }

  return undefined;
}

function titleCaseTiny(value: any): string {
  const raw = normalizeAnalyticsText(value);
  if (!raw) return "";
  return raw
    .split(/\s+/)
    .map((part) =>
      part ? `${part.charAt(0).toUpperCase()}${part.slice(1)}` : part,
    )
    .join(" ");
}

function entryMillis(value: any): number {
  if (!value) return 0;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value?.toMillis === "function") {
    try {
      const m = value.toMillis();
      return Number.isFinite(m) ? m : 0;
    } catch {}
  }
  if (typeof value?.toDate === "function") {
    try {
      const d = value.toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d.getTime() : 0;
    } catch {}
  }
  if (value instanceof Date && !Number.isNaN(value.getTime()))
    return value.getTime();
  const parsed = Date.parse(String(value));
  return Number.isNaN(parsed) ? 0 : parsed;
}

function getHoldCatchMillis(entry: any): number {
  return entryMillis(
    pickFirstValue(entry, [
      "catchTimestamp",
      "createdAt",
      "timestamp",
      "date",
      "catchDate",
      "createdAtMs",
      "updatedAt",
    ]),
  );
}

function inferTimeWindowFromEntry(entry: any): string {
  const explicit = normalizeAnalyticsText(
    pickFirstValue(entry, [
      "timeBucket",
      "window",
      "fishIQTimeBucket",
      "period",
      "daypart",
    ]),
  );
  if (explicit) return titleCaseTiny(explicit);

  const rawTime = normalizeAnalyticsText(
    pickFirstValue(entry, ["time", "catchTime", "hour"]),
  );
  let hour: number | null = null;

  if (rawTime) {
    const normalized = rawTime.trim().toLowerCase();
    const amPmMatch = normalized.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)/);
    if (amPmMatch) {
      const base = Number(amPmMatch[1]);
      if (Number.isFinite(base)) {
        hour = base % 12;
        if (amPmMatch[3] === "pm") hour += 12;
      }
    } else {
      const h = Number(normalized.split(":")[0]);
      if (Number.isFinite(h)) hour = h;
    }
  }

  if (hour === null) {
    const ms = getHoldCatchMillis(entry);
    if (ms) hour = new Date(ms).getHours();
  }

  if (hour === null) return "";
  if (hour >= 4 && hour < 8) return "Dawn";
  if (hour >= 8 && hour < 12) return "Morning";
  if (hour >= 12 && hour < 17) return "Afternoon";
  if (hour >= 17 && hour < 21) return "Evening";
  return "Night";
}

function pushCount(map: Record<string, number>, value: any) {
  const clean = titleCaseTiny(value);
  if (!clean) return;
  map[clean] = (map[clean] || 0) + 1;
}

function topCounts(map: Record<string, number>, limit = 5) {
  return Object.entries(map)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, limit)
    .map(([label, count]) => ({ label, count }));
}

function getHoldCatchSpecies(entry: any): string {
  return String(
    pickFirstValue(entry, [
      "species",
      "speciesName",
      "fishSpecies",
      "fishType",
      "fish",
      "catchSpecies",
      "fishName",
      "title",
    ]) || "",
  )
    .trim()
    .toLowerCase();
}

function getHoldCatchWater(entry: any): string {
  return normalizeAnalyticsText(
    pickFirstValue(entry, [
      "waterName",
      "locationName",
      "location",
      "water",
      "spotName",
      "placeName",
      "bodyOfWater",
      "waterbody",
    ]),
  );
}

function getHoldCatchLure(entry: any): string {
  return normalizeAnalyticsText(
    pickFirstValue(entry, [
      "lureOrBait",
      "lureOrBaitName",
      "lure",
      "bait",
      "lureName",
      "baitName",
      "technique",
      "method",
    ]),
  );
}

function getHoldCatchImage(entry: any): string {
  const direct = pickFirstValue(entry, [
    "imageUrl",
    "photoUrl",
    "thumbnailUrl",
    "photoURL",
    "image",
  ]);
  if (typeof direct === "string" && direct.trim()) return direct.trim();

  const imageArrays = [
    entry?.images,
    entry?.imageUrls,
    entry?.photoUrls,
    entry?.photos,
    entry?.raw?.images,
    entry?.raw?.imageUrls,
    entry?.raw?.photoUrls,
    entry?.raw?.photos,
  ];

  for (const bucket of imageArrays) {
    if (Array.isArray(bucket)) {
      const first = bucket.find((v) => typeof v === "string" && v.trim());
      if (first) return first.trim();
    }
  }

  return "";
}

function getHoldCatchKey(entry: any, index: number): string {
  const sourceRef = normalizeAnalyticsText(
    pickFirstValue(entry, [
      "sourcePostId",
      "sourceId",
      "communityPostId",
      "catchId",
      "logId",
      "sourceCatchId",
    ]),
  ).toLowerCase();
  if (sourceRef) return `src:${sourceRef}`;

  const id = normalizeAnalyticsText(pickFirstValue(entry, ["id", "docId"]));
  const source = normalizeAnalyticsText(
    pickFirstValue(entry, ["source", "createdFrom"]),
  );
  if (id) return `${source || "catch"}:${id}`;

  const species = getHoldCatchSpecies(entry);
  const water = getHoldCatchWater(entry).toLowerCase();
  const image = getHoldCatchImage(entry).toLowerCase();
  const weight = normalizeAnalyticsText(
    pickFirstValue(entry, ["weight", "length"]),
  ).toLowerCase();
  const minute = Math.floor(getHoldCatchMillis(entry) / 60000);

  return [species, water, weight, image, minute || index].join("|");
}

function collectUniqueHoldCatches(profile: any): any[] {
  // IMPORTANT: App passes `catchEntries` as the already-normalized, already-
  // deduped Roddo Log timeline. Do not union that array with catchLog/log/diary
  // aliases again or the same physical catches can be counted twice under
  // different wrapper/source ids. Pick the best live inventory bucket once.
  const preferredBucket = [
    profile?.catchEntries,
    profile?.log,
    profile?.catchLog,
    profile?.catchDiaryEntries,
    profile?.catchDiary,
    profile?.catchDiaryLogs,
    profile?.catchHistory,
    profile?.catches,
  ].find((bucket) => Array.isArray(bucket));

  if (!Array.isArray(preferredBucket)) return [];

  const seen = new Set<string>();
  const out: any[] = [];

  preferredBucket.forEach((entry: any, index: number) => {
    if (!entry || typeof entry !== "object") return;
    const key = getHoldCatchKey(entry, index).trim();
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(entry);
  });

  return out.sort((a, b) => getHoldCatchMillis(b) - getHoldCatchMillis(a));
}

function countUniqueFromEntries(
  entries: any[],
  getter: (entry: any) => any,
): number {
  const values = new Set<string>();
  (entries || []).forEach((entry) => {
    const clean = normalizeAnalyticsText(getter(entry)).toLowerCase();
    if (clean) values.add(clean);
  });
  return values.size;
}

function getGearCatchCount(entries: any[], gearItems: any[]): number {
  const gearIds = new Set(
    (gearItems || [])
      .map((g: any) => normalizeAnalyticsText(g?.id))
      .filter(Boolean),
  );
  const gearNames = new Set(
    (gearItems || [])
      .map((g: any) =>
        normalizeAnalyticsText(g?.name || g?.title).toLowerCase(),
      )
      .filter(Boolean),
  );

  return (entries || []).filter((entry: any) => {
    const ids = [
      pickFirstValue(entry, ["rodId"]),
      pickFirstValue(entry, ["reelId"]),
      pickFirstValue(entry, ["lureId"]),
      pickFirstValue(entry, ["lureOrBaitId"]),
      pickFirstValue(entry, ["gearId"]),
      pickFirstValue(entry, ["tackleBoxItemId"]),
    ]
      .map(normalizeAnalyticsText)
      .filter(Boolean);
    if (ids.some((id) => gearIds.has(id))) return true;

    const names = [
      pickFirstValue(entry, ["rod", "rodName"]),
      pickFirstValue(entry, ["reel", "reelName"]),
      pickFirstValue(entry, ["lure", "lureName"]),
      pickFirstValue(entry, ["bait", "baitName"]),
      pickFirstValue(entry, ["lureOrBait", "lureOrBaitName"]),
    ]
      .map((v) => normalizeAnalyticsText(v).toLowerCase())
      .filter(Boolean);
    return names.some((name) => gearNames.has(name));
  }).length;
}

function buildHoldFishIQSummary(
  userProfile: any,
  gearItems: any[],
  catchesOverride?: any[],
) {
  const catches = (
    Array.isArray(catchesOverride)
      ? catchesOverride
      : collectUniqueHoldCatches(userProfile)
  ).filter(Boolean);

  const bySpecies: Record<string, number> = {};
  const byWindow: Record<string, number> = {};
  const byCondition: Record<string, number> = {};
  const byWater: Record<string, number> = {};
  const byLure: Record<string, number> = {};
  const byGear: Record<string, number> = {};

  const gearNameById = new Map<string, string>();
  (gearItems || []).forEach((item: any) => {
    const id = normalizeAnalyticsText(item?.id);
    const name = normalizeAnalyticsText(item?.name || item?.title);
    if (id && name) gearNameById.set(id, name);
  });

  let insightsAvailable = 0;
  let insightsSeen = 0;
  let insightsDismissed = 0;

  catches.forEach((entry: any) => {
    pushCount(bySpecies, getHoldCatchSpecies(entry));
    pushCount(byWindow, inferTimeWindowFromEntry(entry));
    pushCount(byWater, getHoldCatchWater(entry));
    pushCount(byLure, getHoldCatchLure(entry));

    const conditionRaw = [
      pickFirstValue(entry, ["weather"]),
      pickFirstValue(entry, ["waterCondition"]),
      pickFirstValue(entry, ["conditions"]),
    ]
      .filter(Boolean)
      .join(" • ");
    normalizeAnalyticsText(conditionRaw)
      .split(/[•,|/]+/)
      .map((v) => v.trim())
      .filter(Boolean)
      .slice(0, 3)
      .forEach((v) => pushCount(byCondition, v));

    [
      pickFirstValue(entry, ["rodId"]),
      pickFirstValue(entry, ["reelId"]),
      pickFirstValue(entry, ["lureId"]),
      pickFirstValue(entry, ["lureOrBaitId"]),
    ].forEach((id) => {
      const cleanId = normalizeAnalyticsText(id);
      if (cleanId && gearNameById.has(cleanId))
        pushCount(byGear, gearNameById.get(cleanId));
    });
    [
      pickFirstValue(entry, ["rod", "rodName"]),
      pickFirstValue(entry, ["reel", "reelName"]),
      pickFirstValue(entry, ["lureOrBait", "lureOrBaitName"]),
      pickFirstValue(entry, ["lure", "lureName"]),
      pickFirstValue(entry, ["bait", "baitName"]),
    ].forEach((name) => pushCount(byGear, name));

    if (
      pickFirstValue(entry, [
        "fishIQInsight",
        "insight",
        "insightSummary",
        "fishIQRead",
        "fishIQSummary",
      ])
    )
      insightsAvailable += 1;
    if (
      pickFirstValue(entry, [
        "fishIQInsightSeen",
        "insightSeen",
        "hasOpenedInsight",
      ])
    )
      insightsSeen += 1;
    if (
      pickFirstValue(entry, [
        "fishIQInsightDismissed",
        "insightDismissed",
        "hideInsight",
      ])
    )
      insightsDismissed += 1;
  });

  const sampleSize = catches.length;
  const signalLabel =
    sampleSize >= 12
      ? "Personal pattern forming"
      : sampleSize >= 4
        ? "Early personal signal"
        : "Needs more catches";

  return {
    sampleSize,
    signalLabel,
    species: topCounts(bySpecies),
    windows: topCounts(byWindow),
    conditions: topCounts(byCondition),
    waters: topCounts(byWater),
    lures: topCounts(byLure),
    gear: topCounts(byGear),
    insightsAvailable,
    insightsSeen,
    insightsDismissed,
  };
}

function normalizeStateToCode(input?: string | null): string | null {
  if (!input) return null;
  const raw = String(input).trim();
  if (!raw) return null;
  const up = raw.toUpperCase();
  if (/^[A-Z]{2}$/.test(up)) return up;
  return US_STATE_NAME_TO_CODE[raw.toLowerCase()] || null;
}

function useRoddoScrollGlow<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof window === "undefined") return;

    const findScrollParent = (): HTMLElement | Window => {
      let el: HTMLElement | null = node.parentElement;
      while (el && el !== document.body) {
        const style = window.getComputedStyle(el);
        const canScroll = /(auto|scroll|overlay)/.test(style.overflowY);
        if (canScroll && el.scrollHeight > el.clientHeight) return el;
        el = el.parentElement;
      }
      return window;
    };

    const scroller = findScrollParent();
    let raf = 0;

    const readScroll = () => {
      if (scroller === window) {
        const doc = document.scrollingElement || document.documentElement;
        return {
          top: window.scrollY || doc.scrollTop || 0,
          max: Math.max(1, doc.scrollHeight - window.innerHeight),
        };
      }
      const el = scroller as HTMLElement;
      return {
        top: el.scrollTop,
        max: Math.max(1, el.scrollHeight - el.clientHeight),
      };
    };

    const update = () => {
      raf = 0;
      const { top, max } = readScroll();
      const progress = Math.max(0, Math.min(1, top / max));
      const eased = 1 - Math.pow(1 - progress, 2);
      const x = 48 + Math.sin(progress * Math.PI) * 8;
      const y = 12 + eased * 58;
      const opacity = 0.72 + progress * 0.18;

      node.style.setProperty("--roddo-scroll-glow-x", `${x.toFixed(1)}%`);
      node.style.setProperty("--roddo-scroll-glow-y", `${y.toFixed(1)}%`);
      node.style.setProperty("--roddo-scroll-glow-opacity", opacity.toFixed(2));
    };

    const requestUpdate = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(update);
    };

    update();
    const target = scroller === window ? window : (scroller as HTMLElement);
    target.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate, { passive: true });

    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      target.removeEventListener("scroll", requestUpdate as EventListener);
      window.removeEventListener("resize", requestUpdate as EventListener);
    };
  }, []);

  return ref;
}

function getRoddoScrollGlowStyle(isDarkMode: boolean): React.CSSProperties {
  const glow = isDarkMode
    ? "rgba(118, 85, 220, 0.13)"
    : "rgba(255, 214, 150, 0.18)";
  const glowSoft = isDarkMode
    ? "rgba(146, 120, 232, 0.075)"
    : "rgba(103, 232, 249, 0.075)";

  return {
    backgroundImage: `radial-gradient(720px 420px at var(--roddo-scroll-glow-x, 50%) var(--roddo-scroll-glow-y, 18%), ${glow}, transparent 70%), radial-gradient(520px 360px at calc(var(--roddo-scroll-glow-x, 50%) + 18%) calc(var(--roddo-scroll-glow-y, 18%) + 16%), ${glowSoft}, transparent 72%)`,
    backgroundRepeat: "no-repeat",
    transition: "background-image 220ms ease",
  };
}

const TackleBoxView: React.FC<TackleBoxViewProps> = ({
  userProfile,
  navigateTo,
  addToast,
  onOpenCatchHistory,
  viewState,
  isDarkMode: isDarkModeProp,
}) => {
  const portalTarget = useRoutePortalTarget();
  const { t } = useTranslation();
  const isDarkMode = resolveRoddoDarkMode(isDarkModeProp);
  const theme = getRoddoTheme(isDarkMode);
  const ui = getRoddoUiTokens(isDarkMode);
  const scrollGlowRef = useRoddoScrollGlow<HTMLDivElement>();
  const searchLuresSectionRef = useRef<HTMLDivElement | null>(null);
  const scrollGlowStyle = useMemo(
    () => getRoddoScrollGlowStyle(isDarkMode),
    [isDarkMode],
  );

  const safeToast = (message: string, type: ToastType = "info") => {
    if (typeof addToast === "function") addToast(message, type);
    else console.warn("[TackleBoxView] toast:", message, type);
  };

  const completeGearTask = () => {
    void completeDailyTaskAction("equip_gear")
      .then((result) => {
        if (result?.completed && !result?.alreadyCompleted) {
          const shells = Math.max(0, Math.floor(Number(result.awardedShells || 0)));
          const title = String(result.dailyTask?.title || "Today's Cast task");
          safeToast(`${title} complete — +${shells} Shell${shells === 1 ? "" : "s"}`, "success");
        }
      })
      .catch((err) => console.warn("[DailyTask] equip_gear skipped", err));
  };

  const go = (view: View, state: Record<string, any> = {}) => {
    if (typeof navigateTo === "function") return navigateTo(view, state);
    console.error("[TackleBoxView] navigateTo missing; tried to go to:", view);
    safeToast(
      t(
        "common.navigationNotReady",
        "Navigation is not wired yet for this screen (navigateTo missing).",
      ) as string,
      "error",
    );
  };

  const [activeTab, setActiveTab] = useState<TopTab>("tackle");
  const [gearItems, setGearItems] = useState<TackleBoxItem[]>(
    (userProfile as any).tackleBox ?? [],
  );
  const [gearLoaded, setGearLoaded] = useState(false);
  const [isAddGearOpen, setIsAddGearOpen] = useState(false);
  const [openEquipPicker, setOpenEquipPicker] = useState<PickerSlot>(null);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [licenseMenuOpen, setLicenseMenuOpen] = useState(false);
  const [licenseJurisdictionPickerOpen, setLicenseJurisdictionPickerOpen] = useState(false);
  const [gearMenuOpen, setGearMenuOpen] = useState(false);
  const [licenseMenuPos, setLicenseMenuPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [gearMenuPos, setGearMenuPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const licenseMenuBtnRef = useRef<HTMLButtonElement | null>(null);
  const gearMenuBtnRef = useRef<HTMLButtonElement | null>(null);
  const profileCountryCode = normalizeCountryToCode(
    (userProfile as any)?.countryCode ||
      (userProfile as any)?.country ||
      (userProfile as any)?.homeCountry ||
      (userProfile as any)?.address?.countryCode ||
      (userProfile as any)?.address?.country ||
      null,
  );
  const initialLicenseCountry = profileCountryCode || detectBrowserCountryCode() || "US";
  const [licenseCountryCode, setLicenseCountryCode] = useState(initialLicenseCountry);
  const [licenseRegionCode, setLicenseRegionCode] = useState("");
  const [licenseCountryQuery, setLicenseCountryQuery] = useState("");

  const [localEquipped, setLocalEquipped] = useState<EquippedState>(() =>
    normalizeEquippedForUi((userProfile as any)?.equippedGear),
  );
  const [isClearEquippedConfirmOpen, setIsClearEquippedConfirmOpen] = useState(false);
  const [isClearingEquipped, setIsClearingEquipped] = useState(false);
  const [ownedCosmetics, setOwnedCosmetics] = useState<
    Record<string, OwnedCosmeticRecord>
  >({});
  const [localCosmeticLoadout, setLocalCosmeticLoadout] =
    useState<UserCosmeticLoadout | null>(
      () =>
        ((userProfile as any)?.cosmeticLoadout ||
          null) as UserCosmeticLoadout | null,
    );
  const [avatarShopFilter, setAvatarShopFilter] =
    useState<AvatarShopFilter>("all");
  const [equippingCosmeticId, setEquippingCosmeticId] = useState<string | null>(
    null,
  );
  const [purchasingCosmeticId, setPurchasingCosmeticId] = useState<
    string | null
  >(null);
  const [purchasingLureBundleId, setPurchasingLureBundleId] = useState<SearchLureBundleId | null>(null);
  const [pendingShellPurchase, setPendingShellPurchase] = useState<PendingShellPurchase | null>(null);
  const [purchaseSuccess, setPurchaseSuccess] = useState<PurchaseSuccessState | null>(null);
  const pendingEquippedRef = useRef<
    Partial<Record<EquippedSlot, string | null>>
  >({});

  useEffect(() => {
    const tab = (viewState as any)?.tab;
    if (tab === "tackle" || tab === "stats" || tab === "avatar") setActiveTab(tab);
    else if (tab === "tips") setActiveTab("stats");
  }, [viewState]);

  useEffect(() => {
    if ((viewState as any)?.focus !== "searchLures") return;

    // The incoming route first opens the Avatar tab, then this second pass
    // lands the user on the Search Lures purchase card once it is mounted.
    const timer = window.setTimeout(() => {
      searchLuresSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 120);

    return () => window.clearTimeout(timer);
  }, [viewState, activeTab]);

  useEffect(() => {
    if (!userProfile?.uid) {
      setGearItems([]);
      setGearLoaded(false);
      return;
    }

    setGearLoaded(false);
    const unsubscribe = subscribeToGear(
      userProfile.uid,
      (items) => {
        setGearItems(items);
        setGearLoaded(true);
      },
      (err) => {
        console.error("[TackleBoxView] subscribeToGear error:", err);
        setGearLoaded(true);
      },
    );

    return () => unsubscribe();
  }, [userProfile?.uid]);

  useEffect(() => {
    if (!userProfile?.uid) return;

    // Equipped gear lives on the user doc, while the gear list lives in /users/{uid}/gear.
    // Listen to the user doc directly so the active rod/reel/lure does not snap back
    // when App-level userProfile props are stale during tab/view remounts.
    const unsubscribe = subscribeToEquippedGear(
      userProfile.uid,
      (equipped) => {
        const normalized = normalizeEquippedForUi(equipped);
        pendingEquippedRef.current = Object.fromEntries(
          Object.entries(pendingEquippedRef.current).filter(
            ([key, value]) => (normalized as any)[key] !== value,
          ),
        ) as Partial<Record<EquippedSlot, string | null>>;
        setLocalEquipped(
          applyPendingEquipped(normalized, pendingEquippedRef.current),
        );
      },
      (err) => {
        console.error("[TackleBoxView] subscribeToEquippedGear error:", err);
      },
    );

    return () => unsubscribe();
  }, [userProfile?.uid]);

  const profileCosmeticLoadoutKey = cosmeticLoadoutKey(
    (userProfile as any)?.cosmeticLoadout,
  );

  useEffect(() => {
    setLocalCosmeticLoadout(
      ((userProfile as any)?.cosmeticLoadout ||
        null) as UserCosmeticLoadout | null,
    );
  }, [userProfile?.uid, profileCosmeticLoadoutKey]);

  useEffect(() => {
    if (!userProfile?.uid) {
      setOwnedCosmetics({});
      return;
    }

    let mounted = true;
    ensureStarterCosmetics(userProfile.uid).catch((err) => {
      console.warn("[TackleBoxView] ensureStarterCosmetics failed:", err);
    });

    const unsubscribe = subscribeToOwnedCosmetics(
      userProfile.uid,
      (items) => {
        if (mounted) setOwnedCosmetics(items);
      },
      (err) => {
        console.error("[TackleBoxView] subscribeToOwnedCosmetics error:", err);
      },
    );

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [userProfile?.uid]);

  useEffect(() => {
    if (!lightboxUrl) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightboxUrl(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [lightboxUrl]);

  useEffect(() => {
    if (!licenseMenuOpen) return;
    const onKey = (e: KeyboardEvent) =>
      e.key === "Escape" && setLicenseMenuOpen(false);
    const onScroll = () => setLicenseMenuOpen(false);
    const onResize = () => setLicenseMenuOpen(false);
    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [licenseMenuOpen]);

  useEffect(() => {
    if (!licenseJurisdictionPickerOpen || typeof document === "undefined") return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLicenseJurisdictionPickerOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [licenseJurisdictionPickerOpen]);

  useEffect(() => {
    if (!gearMenuOpen) return;
    const onKey = (e: KeyboardEvent) =>
      e.key === "Escape" && setGearMenuOpen(false);
    const onScroll = () => setGearMenuOpen(false);
    const onResize = () => setGearMenuOpen(false);
    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [gearMenuOpen]);

  const tackleBox: TackleBoxItem[] = gearItems;
  const licenses: UserLicense[] = ((userProfile as any).licenses ??
    (userProfile as any).userLicenses ??
    (userProfile as any).licenseCards ??
    []) as UserLicense[];

  const rods = useMemo(
    () => tackleBox.filter((i) => i.category === "rod"),
    [tackleBox],
  );
  const reels = useMemo(
    () => tackleBox.filter((i) => i.category === "reel"),
    [tackleBox],
  );
  const luresBaits = useMemo(
    () =>
      tackleBox.filter((i) => i.category === "lure" || i.category === "bait"),
    [tackleBox],
  );

  useEffect(() => {
    if (!gearLoaded) return;

    setLocalEquipped((prev) => {
      const next = {
        rodId:
          prev.rodId && rods.some((r) => r.id === prev.rodId)
            ? prev.rodId
            : null,
        reelId:
          prev.reelId && reels.some((r) => r.id === prev.reelId)
            ? prev.reelId
            : null,
        lureOrBaitId:
          prev.lureOrBaitId &&
          luresBaits.some((l) => l.id === prev.lureOrBaitId)
            ? prev.lureOrBaitId
            : null,
      };

      if (
        next.rodId === prev.rodId &&
        next.reelId === prev.reelId &&
        next.lureOrBaitId === prev.lureOrBaitId
      ) {
        return prev;
      }

      return next;
    });
  }, [gearLoaded, rods, reels, luresBaits]);

  const equippedRod = rods.find((r) => r.id === localEquipped.rodId) || null;
  const equippedReel = reels.find((r) => r.id === localEquipped.reelId) || null;
  const equippedLureBait =
    luresBaits.find((l) => l.id === localEquipped.lureOrBaitId) || null;

  const completedChallengesRaw =
    (userProfile as any)?.completedChallengesCount ??
    (userProfile as any)?.completedChallengesMap ??
    (userProfile as any)?.challengeCompletionMap ??
    (userProfile as any)?.completedChallenges;
  const completedChallenges =
    typeof completedChallengesRaw === "number"
      ? completedChallengesRaw
      : Array.isArray(completedChallengesRaw)
        ? completedChallengesRaw.length
        : completedChallengesRaw && typeof completedChallengesRaw === "object"
          ? Object.keys(completedChallengesRaw).length
          : Number((userProfile as any)?.stats?.completedChallenges ?? 0);

  const uniqueCatches = useMemo(
    () => collectUniqueHoldCatches(userProfile as any),
    [userProfile],
  );

  const hasLiveCatchInventory = [
    (userProfile as any)?.catchEntries,
    (userProfile as any)?.log,
    (userProfile as any)?.catchLog,
    (userProfile as any)?.catchDiaryEntries,
    (userProfile as any)?.catchDiary,
  ].some((bucket) => Array.isArray(bucket));

  const canonicalStats = (userProfile as any)?.stats || {};
  const hasCanonicalStats =
    canonicalStats?.source === "catch_inventory_v2" &&
    Number(canonicalStats?.schemaVersion || 0) >= 2;

  // The Hold is an inventory view, not a lifetime progression view. When live
  // catch arrays are present, derive every inventory stat from that one current
  // timeline. Only fall back to the server's canonical v2 public projection if
  // the live arrays are not available at all.
  const totalCatches: number = hasLiveCatchInventory
    ? uniqueCatches.length
    : hasCanonicalStats
      ? Math.max(0, Number(canonicalStats?.catches ?? canonicalStats?.totalCatches) || 0)
      : 0;

  const anglerXP = Number(
    (userProfile as any)?.xp ?? (userProfile as any)?.points ?? 0,
  );
  const xpInfo = getXPProgress(anglerXP);
  const anglerLevel = xpInfo.level;

  const uniqueSpeciesCount = hasLiveCatchInventory
    ? countUniqueFromEntries(uniqueCatches, (entry) => getHoldCatchSpecies(entry))
    : hasCanonicalStats
      ? Math.max(0, Number(canonicalStats?.species ?? canonicalStats?.uniqueSpeciesCount) || 0)
      : 0;

  const uniqueWaterCount = hasLiveCatchInventory
    ? countUniqueFromEntries(uniqueCatches, (entry) => getHoldCatchWater(entry))
    : hasCanonicalStats
      ? Math.max(0, Number(canonicalStats?.waters ?? canonicalStats?.spots ?? canonicalStats?.uniqueWatersCount) || 0)
      : 0;
  const gearLinkedCatchCount = getGearCatchCount(
    uniqueCatches,
    gearItems as any[],
  );
  const loadoutSlotsEquipped = [
    localEquipped.rodId,
    localEquipped.reelId,
    localEquipped.lureOrBaitId,
  ].filter(Boolean).length;

  const fishIQSummary = useMemo(
    () =>
      buildHoldFishIQSummary(
        userProfile as any,
        gearItems as any[],
        uniqueCatches,
      ),
    [userProfile, gearItems, uniqueCatches],
  );

  const shellBalance = Math.max(0, Number((userProfile as any)?.shells ?? 0));
  const [searchLureClockMs, setSearchLureClockMs] = useState(() => Date.now());
  const searchLureWallet = getSearchLureWalletPreview(
    userProfile as any,
    searchLureClockMs,
  );
  const searchLureBalance = searchLureWallet.balance;
  const searchLureRefillLabel = getSearchLureRefillLabel(
    searchLureWallet,
    searchLureClockMs,
  );
  const [rewardedAdBusy, setRewardedAdBusy] = useState(false);
  const rewardedSearchLureAvailable = canOfferRewardedSearchLureAd();

  const handleRewardedSearchLure = async () => {
    if (rewardedAdBusy) return;
    setRewardedAdBusy(true);
    try {
      const result = await showRewardedSearchLureAd();
      if (result.ok && result.earned) {
        if (result.testMode) {
          safeToast("Test ad completed. Test ads never grant Search Lures.", "success");
          return;
        }

        if (result.serverStatus === "granted") {
          safeToast(`+${result.rewardAmount || REWARDED_SEARCH_LURE_AMOUNT} Search Lures added.`, "success");
          setSearchLureClockMs(Date.now());
          return;
        }

        if (result.serverStatus === "pending") {
          safeToast("Ad completed. Your Search Lures are being verified and will appear shortly.", "success");
          return;
        }

        if (result.serverStatus === "capped") {
          safeToast("Daily rewarded-ad limit reached. No additional Search Lures were granted.", "error");
          return;
        }

        safeToast("Ad completed, but this reward was already processed.", "success");
        return;
      }

      if ("reason" in result && result.reason === "rewarded_daily_cap_reached") {
        safeToast(`Daily rewarded-ad limit reached (${REWARDED_SEARCH_LURE_DAILY_CAP}/day).`, "error");
      } else {
        safeToast("Rewarded ad is unavailable right now. Try again later.", "error");
      }
    } finally {
      setRewardedAdBusy(false);
    }
  };

  useEffect(() => {
    const timer = window.setInterval(
      () => setSearchLureClockMs(Date.now()),
      60_000,
    );
    return () => window.clearInterval(timer);
  }, []);
  const lifetimeShells = Math.max(
    shellBalance,
    Number((userProfile as any)?.lifetimeShells ?? shellBalance),
  );
  const starterAvatarGear = useMemo(() => getStarterCosmetics(), []);
  const ownedCosmeticIdSet = useMemo(() => {
    const set = new Set<string>(starterAvatarGear.map((item) => item.id));
    Object.keys(ownedCosmetics || {}).forEach((id) => set.add(id));
    return set;
  }, [ownedCosmetics, starterAvatarGear]);
  const avatarGearPreview = useMemo(
    () => getLoadoutItems(localCosmeticLoadout),
    [localCosmeticLoadout],
  );
  const avatarGearCatalog = useMemo(
    () =>
      cosmeticCatalog.filter(
        (item) => item.starter || ownedCosmeticIdSet.has(item.id),
      ),
    [ownedCosmeticIdSet],
  );
  const previewShopCosmetics = useMemo(
    () =>
      getPreviewShopCosmetics(anglerLevel, shellBalance).filter(
        (item) => !ownedCosmeticIdSet.has(item.id),
      ),
    [anglerLevel, shellBalance, ownedCosmeticIdSet],
  );

  const avatarShopFilters = useMemo(
    () =>
      [
        { key: "all" as AvatarShopFilter, label: "All" },
        { key: "head" as AvatarShopFilter, label: "Head" },
        { key: "outfit" as AvatarShopFilter, label: "Outfit" },
        { key: "gear" as AvatarShopFilter, label: "Gear" },
        { key: "identity" as AvatarShopFilter, label: "Identity" },
      ],
    [],
  );

  const cosmeticMatchesAvatarFilter = (
    slot: CosmeticSlot,
    filter: AvatarShopFilter,
  ) => {
    if (filter === "all") return true;
    if (filter === "head") return ["hat", "hair", "faceCover"].includes(slot);
    if (filter === "outfit")
      return ["top", "outerwear", "pants", "socks", "footwear"].includes(slot);
    if (filter === "gear")
      return ["rodSkin", "reelSkin", "lureCharm"].includes(slot);
    return ["profileFrame", "background", "title", "badgeA", "badgeB", "badgeC"].includes(slot);
  };

  const filteredOwnedAvatarGear = useMemo(
    () =>
      avatarGearCatalog.filter((item) =>
        cosmeticMatchesAvatarFilter(item.slot as CosmeticSlot, avatarShopFilter),
      ),
    [avatarGearCatalog, avatarShopFilter],
  );

  const filteredShopCosmetics = useMemo(
    () =>
      previewShopCosmetics.filter((item) =>
        cosmeticMatchesAvatarFilter(item.slot as CosmeticSlot, avatarShopFilter),
      ),
    [previewShopCosmetics, avatarShopFilter],
  );

  const handleToggleAvatarCosmetic = async (cosmeticId: string) => {
    if (!userProfile?.uid || equippingCosmeticId) return;
    const item = getCosmeticById(cosmeticId);
    if (!item) return;

    const resolved = resolveCosmeticLoadout(localCosmeticLoadout) as any;
    const isCurrentlyEquipped = resolved?.[item.slot] === item.id;

    setEquippingCosmeticId(cosmeticId);
    const previous = localCosmeticLoadout;
    setLocalCosmeticLoadout((prev) => ({
      ...(resolveCosmeticLoadout(prev) as any),
      [item.slot]: isCurrentlyEquipped ? null : item.id,
    }));

    try {
      if (isCurrentlyEquipped) {
        const authoritativeLoadout = await unequipAvatarCosmeticSlot(
          userProfile.uid,
          item.slot as CosmeticSlot,
        );
        setLocalCosmeticLoadout(authoritativeLoadout);
        // Launch quiet pass: equipped state updates visually, no routine toast needed.
      } else {
        const authoritativeLoadout = await equipAvatarCosmetic(
          userProfile.uid,
          item.id,
        );
        setLocalCosmeticLoadout(authoritativeLoadout);
        // Launch quiet pass: equipped state updates visually; daily task toast still fires when relevant.
        completeGearTask();
      }
    } catch (err) {
      console.error("[TackleBoxView] toggle avatar cosmetic failed:", err);
      setLocalCosmeticLoadout(previous || null);
      safeToast("Could not update that avatar gear yet.", "error");
    } finally {
      setEquippingCosmeticId(null);
    }
  };

  const requestPurchaseAvatarCosmetic = (cosmeticId: string) => {
    if (!userProfile?.uid || purchasingCosmeticId || purchasingLureBundleId) return;
    const item = getCosmeticById(cosmeticId);
    if (!item) return;

    setPurchaseSuccess(null);
    setPendingShellPurchase({
      kind: "cosmetic",
      cosmeticId: item.id,
      name: item.name,
      emoji: item.emoji || "🎣",
      costShells: Math.max(0, Math.floor(Number(item.costShells || 0))),
    });
  };

  const requestPurchaseSearchLures = (bundleId: SearchLureBundleId) => {
    if (!userProfile?.uid || purchasingCosmeticId || purchasingLureBundleId) return;
    const bundle = SEARCH_LURE_BUNDLES.find((item) => item.id === bundleId);
    if (!bundle) return;

    setPurchaseSuccess(null);
    setPendingShellPurchase({
      kind: "search_lures",
      bundleId: bundle.id,
      name: bundle.name,
      quantity: bundle.quantity,
      costShells: bundle.costShells,
    });
  };

  const confirmPendingShellPurchase = async () => {
    if (!userProfile?.uid || !pendingShellPurchase) return;
    if (purchasingCosmeticId || purchasingLureBundleId) return;

    const pending = pendingShellPurchase;

    if (pending.kind === "cosmetic") {
      setPurchasingCosmeticId(pending.cosmeticId);
      try {
        const result = await purchaseAvatarCosmetic(pending.cosmeticId);
        const nextShellBalance = Math.max(
          0,
          Math.floor(Number(result.shellBalance ?? result.shells ?? shellBalance - pending.costShells)),
        );

        setPendingShellPurchase(null);
        setPurchaseSuccess({
          title: `${pending.name} added to your collection`,
          description: "Purchase complete. You can equip it from Owned cosmetics whenever you want.",
          icon: pending.emoji,
          shellBalance: nextShellBalance,
        });
      } catch (err: any) {
        console.error("[TackleBoxView] purchaseAvatarCosmetic failed:", err);
        const code = String(err?.code || "").toLowerCase();
        const message = String(
          err?.message || "Could not buy that avatar gear yet.",
        );
        safeToast(
          code.includes("failed-precondition") || /shell/i.test(message)
            ? "Not enough Shells yet."
            : message,
          "error",
        );
      } finally {
        setPurchasingCosmeticId(null);
      }
      return;
    }

    setPurchasingLureBundleId(pending.bundleId);
    try {
      const result = await purchaseSearchLureBundle(pending.bundleId);
      setPendingShellPurchase(null);
      setPurchaseSuccess({
        title: `${result.quantity} Search Lures ready`,
        description: "Refill complete. Your next manual Big Blue area searches are ready to cast.",
        icon: "🪝",
        shellBalance: Math.max(0, Math.floor(Number(result.shellBalance || 0))),
        searchLureBalance: Math.max(0, Math.floor(Number(result.searchLureBalance || 0))),
      });
    } catch (err: any) {
      console.error('[TackleBoxView] purchaseSearchLureBundle failed:', err);
      const message = String(err?.message || 'Could not refill Search Lures yet.');
      safeToast(/shell/i.test(message) ? 'Not enough Shells yet.' : message, 'error');
    } finally {
      setPurchasingLureBundleId(null);
    }
  };

  const fishIQSampleCount = Math.max(fishIQSummary.sampleSize, totalCatches);
  const fishIQSignalLabel =
    fishIQSummary.sampleSize > 0
      ? fishIQSummary.signalLabel
      : fishIQSampleCount > 0
        ? "Catch history linked"
        : "Needs catches";
  const fishIQSampleLabel =
    fishIQSummary.sampleSize > 0
      ? `${fishIQSummary.sampleSize} catches analyzed`
      : fishIQSampleCount > 0
        ? `${fishIQSampleCount} catches recorded`
        : "No catches yet";
  const topSpeciesLabel =
    fishIQSummary.species[0]?.label ||
    (uniqueSpeciesCount > 0
      ? `${uniqueSpeciesCount} species logged`
      : "Still learning");
  const topWindowLabel =
    fishIQSummary.windows[0]?.label ||
    (fishIQSampleCount > 0 ? "Add catch times" : "Needs catches");
  const topLureLabel =
    fishIQSummary.lures[0]?.label ||
    fishIQSummary.gear[0]?.label ||
    "Tag lure/gear";
  const topWaterLabel =
    fishIQSummary.waters[0]?.label ||
    (uniqueWaterCount > 0 ? `${uniqueWaterCount} waters` : "Add water labels");

  const activeLicenses = useMemo(
    () =>
      (licenses || []).filter(
        (l) =>
          Boolean((l as any)?.active) ||
          String((l as any)?.status || "").toLowerCase() === "active",
      ),
    [licenses],
  );

  const activeLicense = useMemo(
    () => activeLicenses[0] ?? licenses[0] ?? null,
    [activeLicenses, licenses],
  );
  const activeCount = activeLicenses.length;

  const licenseState =
    (activeLicense as any)?.jurisdictionState ||
    (activeLicense as any)?.state ||
    (activeLicense as any)?.jurisdiction ||
    null;

  const detectedLicenseCountryCode =
    normalizeCountryToCode(
      (activeLicense as any)?.countryCode ||
        (activeLicense as any)?.country ||
        (userProfile as any)?.countryCode ||
        (userProfile as any)?.country ||
        (userProfile as any)?.homeCountry ||
        (userProfile as any)?.address?.countryCode ||
        (userProfile as any)?.address?.country ||
        null,
    ) || profileCountryCode || detectBrowserCountryCode() || "US";

  const detectedLicenseRegionCode = normalizeLicenseRegion(
    detectedLicenseCountryCode,
    licenseState ||
      (userProfile as any)?.licenseState ||
      (userProfile as any)?.state ||
      (userProfile as any)?.homeState ||
      (userProfile as any)?.region ||
      (userProfile as any)?.province ||
      (userProfile as any)?.territory ||
      null,
  );

  useEffect(() => {
    setLicenseCountryCode(detectedLicenseCountryCode);
    setLicenseRegionCode(detectedLicenseRegionCode);
  }, [detectedLicenseCountryCode, detectedLicenseRegionCode]);

  const selectedLicenseCountryName = countryNameForCode(licenseCountryCode);
  const selectedLicenseRegionName = licenseRegionCode
    ? regionNameForCode(licenseCountryCode, licenseRegionCode)
    : "";
  const licenseRegionOptions = LICENSE_REGIONS[licenseCountryCode] || [];
  const licenseJurisdictionLabel = selectedLicenseRegionName
    ? `${selectedLicenseRegionName}, ${selectedLicenseCountryName}`
    : selectedLicenseCountryName;
  const filteredLicenseCountries = LICENSE_COUNTRIES.filter((country) => {
    const q = licenseCountryQuery.trim().toLowerCase();
    if (!q) return true;
    return country.name.toLowerCase().includes(q) || country.code.toLowerCase().includes(q);
  });

  const licenseType = (activeLicense as any)?.type || null;
  const licenseExpiry =
    (activeLicense as any)?.expiryDate ??
    (activeLicense as any)?.expiry ??
    (activeLicense as any)?.endDate ??
    null;
  const expiryDate = useMemo(() => toDateSafe(licenseExpiry), [licenseExpiry]);
  const isExpired = useMemo(
    () => !!expiryDate && expiryDate.getTime() < Date.now(),
    [expiryDate],
  );
  const isExpiringSoon = useMemo(() => {
    if (!expiryDate) return false;
    if (expiryDate.getTime() < Date.now()) return false;
    const TWO_WEEKS_MS = 14 * 24 * 60 * 60 * 1000;
    return expiryDate.getTime() - Date.now() <= TWO_WEEKS_MS;
  }, [expiryDate]);
  const showOfficialLink = activeCount === 0 || isExpiringSoon;

  const getLicensePhoto = (l: any): string | null => {
    const raw =
      l?.photoUrl ||
      l?.licensePhotoUrl ||
      l?.imageUrl ||
      l?.licenseImageUrl ||
      l?.photo ||
      l?.image ||
      (Array.isArray(l?.photoUrls) ? l.photoUrls[0] : null) ||
      (Array.isArray(l?.imageUrls) ? l.imageUrls[0] : null) ||
      null;
    if (!raw) return null;
    return isBlobUrl(raw) ? null : String(raw);
  };

  const licenseThumbUrl = useMemo(
    () => getLicensePhoto(activeLicense),
    [activeLicense],
  );

  const handleEquipSlot = async (slot: EquippedSlot, gearId: string | null) => {
    if (!userProfile?.uid) return;
    const prev = localEquipped;
    const pendingPatch = { [slot]: gearId ?? null } as Partial<
      Record<EquippedSlot, string | null>
    >;

    pendingEquippedRef.current = {
      ...pendingEquippedRef.current,
      ...pendingPatch,
    };

    setLocalEquipped((curr) => applyPendingEquipped(curr, pendingPatch));
    try {
      await setEquippedGearSlot(userProfile.uid, slot, gearId);
      setLocalEquipped((curr) => applyPendingEquipped(curr, pendingPatch));
      // Launch quiet pass: equipped state updates visually; daily task toast still fires when relevant.
      if (gearId) completeGearTask();
    } catch (err) {
      console.error("[TackleBoxView] Failed to equip gear:", err);
      Object.keys(pendingPatch).forEach(
        (key) => delete (pendingEquippedRef.current as any)[key],
      );
      setLocalEquipped(prev);
      safeToast(
        t("tackleBox.gear.equipError", "Could not update equipped gear."),
        "error",
      );
    } finally {
      setIsClearingEquipped(false);
    }
  };

  const resolveFishIQView = (): View | null => {
    const v =
      (View as any).FishIQ ??
      (View as any).FishIq ??
      (View as any).FishIQView ??
      (View as any).FishIQHome ??
      null;
    return (v ?? null) as View | null;
  };

  const openFishIQ = () => {
    const v = resolveFishIQView();
    if (!v) {
      safeToast(
        "FishIQ route not found in View enum. Add View.FishIQ (or tell me its name).",
        "error",
      );
      return;
    }
    go(v);
  };

  const openOfficialLicenseSite = () => {
    try {
      const url = buildOfficialLicenseUrl(licenseCountryCode, licenseRegionCode);
      if (typeof window === "undefined") return;
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (!w)
        safeToast(
          "Popup blocked — please allow popups to open the license site.",
          "info",
        );
    } catch (e) {
      console.error(e);
      safeToast("Could not open license site.", "error");
    }
  };

  const clearEquipped = () => {
    setGearMenuOpen(false);
    setIsClearEquippedConfirmOpen(true);
  };

  const confirmClearEquipped = async () => {
    if (!userProfile?.uid) return;

    const previous = localEquipped;
    setIsClearingEquipped(true);
    const cleared = {
      rodId: null,
      reelId: null,
      lureOrBaitId: null,
      lureId: null,
    };

    try {
      setOpenEquipPicker(null);
      pendingEquippedRef.current = {
        ...pendingEquippedRef.current,
        ...cleared,
      };
      setLocalEquipped((curr) => applyPendingEquipped(curr, cleared));
      await setEquippedGearPatch(userProfile.uid, cleared);
      setIsClearEquippedConfirmOpen(false);
      safeToast(
        t("tackleBox.gear.clearSuccess", "Equipped gear cleared."),
        "success",
      );
    } catch (err) {
      console.error("[TackleBoxView] Failed to clear equipped gear:", err);
      Object.keys(cleared).forEach(
        (key) => delete (pendingEquippedRef.current as any)[key],
      );
      setLocalEquipped(previous);
      safeToast(
        t("tackleBox.gear.equipError", "Could not update equipped gear."),
        "error",
      );
    } finally {
      setIsClearingEquipped(false);
    }
  };

  const openOverflowMenu = (
    btn: HTMLButtonElement | null,
    setPos: (v: { top: number; left: number } | null) => void,
    setOpen: (v: boolean) => void,
  ) => {
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const width = 260;
    setPos({
      top: r.bottom + 10,
      left: Math.min(
        window.innerWidth - width - 12,
        Math.max(12, r.right - width),
      ),
    });
    setOpen(true);
  };

  const edgeLane = "-mx-3.5 min-[390px]:-mx-5 sm:-mx-6";

  const shellTextStyle: React.CSSProperties = { color: ui.textPrimary };
  const cardStyle: React.CSSProperties = {
    background: isDarkMode ? "#101827" : (ui.cardBgStrong ?? ui.cardBg),
    border: `1px solid ${isDarkMode ? "rgba(132,151,190,0.17)" : ui.border}`,
    boxShadow: isDarkMode
      ? "0 6px 16px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.025)"
      : "0 8px 20px rgba(89,65,37,0.065), inset 0 1px 0 rgba(255,255,255,0.58)",
  };
  const mutedCardStyle: React.CSSProperties = {
    background: isDarkMode ? "#0F1828" : ui.cardBgMuted,
    border: `1px solid ${isDarkMode ? "rgba(132,151,190,0.13)" : ui.border}`,
    boxShadow: isDarkMode
      ? "0 5px 14px rgba(0,0,0,0.13), inset 0 1px 0 rgba(255,255,255,0.025)"
      : "0 6px 16px rgba(89,65,37,0.06), inset 0 1px 0 rgba(255,255,255,0.52)",
  };
  const menuStyle: React.CSSProperties = {
    background: ui.menuBg,
    border: `1px solid ${ui.borderStrong}`,
    boxShadow: isDarkMode
      ? "0 16px 34px rgba(3,0,16,0.30), inset 0 1px 0 rgba(255,255,255,0.06)"
      : "0 14px 30px rgba(89,65,37,0.10), inset 0 1px 0 rgba(255,255,255,0.68)",
    color: ui.textPrimary,
  };
  const sectionTitleStyle: React.CSSProperties = {
    color: isDarkMode ? ui.textPrimary : "#241A12",
    textShadow: isDarkMode
      ? "0 1px 0 rgba(0,0,0,0.18)"
      : "0 1px 0 rgba(255,255,255,0.70)",
  };
  const sectionBodyStyle: React.CSSProperties = {
    color: isDarkMode ? ui.textSecondary : "#4D3C2A",
    fontWeight: 700,
  };
  const subtleTextStyle: React.CSSProperties = {
    color: isDarkMode ? ui.textMuted : "#6B553C",
  };

  const tabMeta = [
    {
      key: "tackle" as TopTab,
      label: t("tackleBox.tabs.loadout", "Loadout"),
    },
    {
      key: "stats" as TopTab,
      label: t("tackleBox.tabs.stats", "Stats"),
    },
    {
      key: "avatar" as TopTab,
      label: t("tackleBox.tabs.avatar", "Avatar"),
    },
  ];

  const statCards = [
    {
      label: "CHALLENGES",
      value: completedChallenges,
      body: "Progress, rewards, and leaderboard bragging rights.",
      accent: theme.rewardGold,
      accentText: theme.rewardGoldText,
      cta: "Go →",
      onClick: () =>
        go(
          ((View as any).Challenges ??
            (View as any).Challenge ??
            View.MyPage) as View,
          {
            from: View.TackleBox,
            tab: "stats",
          } as any,
        ),
    },
  ];

  const statMiniCards = [
    {
      label: "Rods",
      value: rods.length,
      accent: theme.rewardBlueSoft,
      text: theme.rewardBlueText,
    },
    {
      label: "Reels",
      value: reels.length,
      accent: theme.rewardMintSoft,
      text: theme.rewardMintText,
    },
    {
      label: "Lures/Baits",
      value: luresBaits.length,
      accent: theme.rewardGoldSoft,
      text: theme.rewardGoldText,
    },
  ];

  const renderEquipTile = (
    label: string,
    item: TackleBoxItem | null,
    slot: Exclude<PickerSlot, null>,
    options: TackleBoxItem[],
    value: string | null | undefined,
    equipKey: "rodId" | "reelId" | "lureOrBaitId",
    accentBg: string,
    accentText: string,
  ) => {
    const slotIcon = slot === "rod" ? "🎣" : slot === "reel" ? "🧵" : "🪱";
    const isEquipped = Boolean(item?.name);

    return (
      <div
        className="relative overflow-hidden rounded-[20px] p-3"
        style={{
          ...mutedCardStyle,
          background: isDarkMode ? "#101827" : "#FFFCF7",
        }}
      >

        <button
          type="button"
          onClick={() => {
            if (options.length > 0)
              setOpenEquipPicker((v) => (v === slot ? null : slot));
            else setIsAddGearOpen(true);
          }}
          className="relative flex w-full items-center gap-3 text-left"
        >
          <div
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[14px] text-xl shadow-sm"
            style={{
              background: accentBg,
              color: accentText,
              border: `1px solid ${ui.border}`,
            }}
          >
            {slotIcon}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className="inline-flex items-center rounded-full px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.18em]"
                style={{ background: accentBg, color: accentText }}
              >
                {label}
              </span>
              <span
                className="inline-flex items-center rounded-full px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.15em]"
                style={{
                  background: isEquipped
                    ? theme.rewardMintSoft
                    : ui.surfaceBgSoft,
                  color: isEquipped ? theme.rewardMintText : ui.textSecondary,
                  border: `1px solid ${ui.border}`,
                }}
              >
                {isEquipped ? "Equipped" : "Open slot"}
              </span>
            </div>

            <p
              className="mt-2 truncate text-sm font-black"
              style={{ color: ui.textPrimary }}
            >
              {item?.name ?? "Nothing selected"}
            </p>
            <p
              className="mt-1 line-clamp-2 text-[11px] font-semibold leading-4"
              style={{ color: ui.textSecondary }}
            >
              {!gearLoaded
                ? "Loading your saved gear…"
                : options.length > 0
                  ? "Tap to switch this slot from your saved Hold."
                  : "No gear here yet. Add one to your hold."}
            </p>
          </div>

          <div
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-lg font-black"
            style={{
              background: ui.surfaceBgSoft,
              color: ui.textPrimary,
              border: `1px solid ${ui.borderStrong}`,
            }}
          >
            {options.length > 0 ? "›" : "+"}
          </div>
        </button>

        {openEquipPicker === slot && options.length > 0 ? (
          <div className="relative mt-3 pl-1">
            <select
              className="w-full rounded-2xl px-3 py-2 text-sm font-bold outline-none"
              style={{
                background: ui.inputBg,
                color: ui.textPrimary,
                border: `1px solid ${ui.borderStrong}`,
                boxShadow: ui.insetHighlight,
              }}
              value={value ?? ""}
              onChange={(e) => {
                handleEquipSlot(equipKey, e.target.value || null);
                setOpenEquipPicker(null);
              }}
            >
              <option value="">
                {t("tackleBox.gear.none", "None equipped")}
              </option>
              {options.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <>
      <RoddoPageShell padding="md" contentClassName="max-w-4xl lg:max-w-6xl roddo-hold-lane">
        <style>{roddoTactilePolishStyles}</style>
        <div
          data-roddo-tour="hold-page"
          data-roddo-tactile
          data-roddo-polish-lane="2"
          className="roddo-hold-polish"
          data-roddo-theme={isDarkMode ? "dark" : "light"}
          ref={scrollGlowRef}
          style={{ ...shellTextStyle, backgroundImage: "none" }}
        >
          <div className="sticky top-0 z-20 pb-3">
            <RoddoPillTabs
              items={tabMeta.map((tab) => ({ key: tab.key, label: tab.label }))}
              activeKey={activeTab}
              onChange={(key) => setActiveTab(key as TopTab)}
              ariaLabel={t("tackleBox.tabs.label", "The Hold") as string}
            />
          </div>

          {activeTab === "avatar" ? (
            <div className="space-y-3.5 pb-6">
              <div className={edgeLane}>
                <div
                  className="roddo-polish-card overflow-hidden rounded-[24px] p-4"
                  style={{
                    ...cardStyle,
                    background: isDarkMode ? "#0D1625" : "#FFFDF8",
                  }}
                >
                  <div className="grid gap-4 min-[430px]:grid-cols-[1fr_194px] min-[430px]:items-center">
                    <div className="min-w-0">
                      <div className="text-[9px] font-black uppercase tracking-[0.18em]" style={{ color: theme.rewardBlueText }}>
                        Angler identity
                      </div>
                      <h2 className="mt-1 text-[22px] font-black leading-tight" style={sectionTitleStyle}>
                        Your Angler
                      </h2>
                      <p className="mt-1 max-w-md text-[10px] font-bold leading-4" style={sectionBodyStyle}>
                        Build the look other anglers see across Roddo.
                      </p>

                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <span
                          className="rounded-full px-2.5 py-1 text-[9px] font-black"
                          style={{ background: theme.rewardMintSoft, color: theme.rewardMintText, border: `1px solid ${ui.border}` }}
                        >
                          {avatarGearPreview.length} equipped
                        </span>
                        <span
                          className="rounded-full px-2.5 py-1 text-[9px] font-black"
                          style={{ background: ui.surfaceBgSoft, color: ui.textSecondary, border: `1px solid ${ui.border}` }}
                        >
                          {avatarGearCatalog.length} owned
                        </span>
                        <span className="text-[9px] font-black" style={subtleTextStyle}>
                          Lv {anglerLevel}
                        </span>
                      </div>

                      <div className="mt-3 grid grid-cols-2 gap-2">
                        <div
                          className="rounded-[12px] px-3 py-2.5"
                          style={{ background: ui.surfaceBgSoft, border: `1px solid ${ui.border}` }}
                        >
                          <div className="text-[8px] font-black uppercase tracking-[0.14em]" style={subtleTextStyle}>Shells</div>
                          <div className="mt-1 text-[17px] font-black leading-none" style={{ color: ui.textPrimary }}>
                            {shellBalance.toLocaleString()} 🐚
                          </div>
                        </div>
                        <div
                          className="rounded-[12px] px-3 py-2.5"
                          style={{ background: theme.rewardBlueSoft, border: `1px solid ${ui.border}` }}
                        >
                          <div className="text-[8px] font-black uppercase tracking-[0.14em]" style={{ color: theme.rewardBlueText }}>Search Lures</div>
                          <div className="mt-1 text-[17px] font-black leading-none" style={{ color: theme.rewardBlueText }}>
                            {searchLureBalance.toLocaleString()} 🪝
                          </div>
                        </div>
                      </div>
                    </div>

                    <div
                      className="relative mx-auto grid h-[188px] w-full max-w-[194px] place-items-center overflow-hidden rounded-[20px]"
                      style={{
                        background: isDarkMode ? "#09111D" : "#F6FAFD",
                        border: `1px solid ${ui.borderStrong}`,
                        boxShadow: isDarkMode
                          ? "inset 0 1px 0 rgba(255,255,255,.035), 0 16px 34px rgba(0,0,0,.22)"
                          : "inset 0 1px 0 rgba(255,255,255,.82), 0 16px 30px rgba(89,65,37,.10)",
                      }}
                    >
                      <div className="absolute left-3 top-3 text-[8px] font-black uppercase tracking-[0.14em]" style={subtleTextStyle}>
                        Live preview
                      </div>
                      <div className="translate-y-2 scale-[1.34]">
                        <MapAvatarMarker
                          profile={{ ...userProfile, cosmeticLoadout: localCosmeticLoadout || userProfile.cosmeticLoadout }}
                          profilePhotoUrl={String(
                            (userProfile as any)?.avatar ||
                            (userProfile as any)?.avatarUrl ||
                            (userProfile as any)?.photoURL ||
                            (userProfile as any)?.profilePhotoUrl ||
                            ""
                          )}
                          isDark={isDarkMode}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 border-t pt-3" style={{ borderColor: ui.border }}>
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-[9px] font-black uppercase tracking-[0.15em]" style={subtleTextStyle}>Current look</div>
                        <div className="mt-0.5 text-[12px] font-black" style={sectionTitleStyle}>
                          {avatarGearPreview.length ? "Equipped identity" : "Starter look"}
                        </div>
                      </div>
                      <span className="text-[8px] font-bold" style={subtleTextStyle}>Live loadout</span>
                    </div>

                    <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
                      {avatarGearPreview.length ? avatarGearPreview.slice(0, 6).map((item) => (
                        <div
                          key={`preview-${item.id}`}
                          className="flex min-w-0 items-center gap-2 rounded-[12px] px-2.5 py-2"
                          style={{ background: ui.surfaceBgSoft, border: `1px solid ${ui.border}` }}
                        >
                          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[9px] text-[16px]" style={{ background: theme.rewardGoldSoft }}>
                            {item.emoji}
                          </span>
                          <div className="min-w-0">
                            <div className="truncate text-[10px] font-black" style={sectionTitleStyle}>{item.name}</div>
                            <div className="truncate text-[8px] font-bold uppercase tracking-[0.09em]" style={subtleTextStyle}>
                              {getCosmeticSlotLabel(item.slot as CosmeticSlot)}
                            </div>
                          </div>
                        </div>
                      )) : (
                        <div className="col-span-2 text-[10px] font-bold sm:col-span-3" style={sectionBodyStyle}>
                          Equip a cosmetic below to build your Angler look.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className={edgeLane}>
                <div
                  className="roddo-polish-card rounded-[20px] p-3.5"
                  style={{ ...cardStyle, background: isDarkMode ? "#101827" : "#FFFCF7" }}
                >
                  <div className="flex items-end justify-between gap-3">
                    <div>
                      <div className="text-[9px] font-black uppercase tracking-[0.16em]" style={subtleTextStyle}>Wardrobe</div>
                      <h3 className="mt-1 text-[16px] font-black" style={sectionTitleStyle}>Locker + Shop</h3>
                    </div>
                    <span className="shrink-0 text-[9px] font-black" style={subtleTextStyle}>
                      {filteredOwnedAvatarGear.length} owned · {filteredShopCosmetics.length} shop
                    </span>
                  </div>

                  <div
                    className="mt-3 flex gap-1.5 overflow-x-auto rounded-[16px] p-1.5"
                    style={{
                      background: isDarkMode ? "#0B1320" : "#F7F1E8",
                      border: `1px solid ${ui.border}`,
                      boxShadow: isDarkMode
                        ? "inset 0 1px 0 rgba(255,255,255,.025)"
                        : "inset 0 1px 0 rgba(255,255,255,.72)",
                    }}
                  >
                    {avatarShopFilters.map((filter) => {
                      const active = avatarShopFilter === filter.key;
                      return (
                        <button
                          key={filter.key}
                          type="button"
                          onClick={() => setAvatarShopFilter(filter.key)}
                          className="shrink-0 rounded-[11px] px-3.5 py-2 text-[9px] font-black uppercase tracking-[0.13em]"
                          style={{
                            background: active
                              ? (isDarkMode ? "#16304A" : "#EAF4FF")
                              : "transparent",
                            color: active
                              ? (isDarkMode ? "#A9EEF5" : "#245F91")
                              : ui.textSecondary,
                            border: active
                              ? `1px solid ${isDarkMode ? "rgba(103,216,232,.28)" : "rgba(47,128,237,.22)"}`
                              : "1px solid transparent",
                            boxShadow: active
                              ? (isDarkMode
                                  ? "0 4px 12px rgba(0,0,0,.18), inset 0 1px 0 rgba(255,255,255,.035)"
                                  : "0 4px 10px rgba(47,128,237,.08), inset 0 1px 0 rgba(255,255,255,.82)")
                              : "none",
                          }}
                        >
                          {filter.label}
                        </button>
                      );
                    })}
                  </div>

                  <div className="mt-4">
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <div className="text-[10px] font-black uppercase tracking-[0.14em]" style={subtleTextStyle}>
                        Owned
                      </div>
                      <span className="text-[8px] font-bold" style={subtleTextStyle}>Tap to equip / remove</span>
                    </div>

                    {filteredOwnedAvatarGear.length ? (
                      <div className="grid gap-2 min-[520px]:grid-cols-2">
                        {filteredOwnedAvatarGear.map((item) => {
                          const resolved = resolveCosmeticLoadout(localCosmeticLoadout) as any;
                          const isEquipped = resolved?.[item.slot] === item.id;
                          const isBusy = equippingCosmeticId === item.id;
                          const rarity = String(item.rarity || "common").toLowerCase();
                          const rarityTone = rarity === "legendary"
                            ? { bg: theme.rewardGoldSoft, fg: theme.rewardGoldText }
                            : rarity === "rare" || rarity === "epic"
                              ? { bg: isDarkMode ? "rgba(103,216,232,0.12)" : "rgba(47,128,237,0.10)", fg: isDarkMode ? "#A9EEF5" : "#245F91" }
                              : rarity === "uncommon"
                                ? { bg: theme.rewardMintSoft, fg: theme.rewardMintText }
                                : { bg: ui.surfaceBgSoft, fg: ui.textSecondary };

                          return (
                            <button
                              key={item.id}
                              type="button"
                              disabled={isBusy}
                              onClick={() => handleToggleAvatarCosmetic(item.id)}
                              className="flex min-w-0 items-center gap-2.5 rounded-[12px] px-3 py-2 text-left disabled:opacity-60"
                              style={{
                                background: isEquipped
                                  ? (isDarkMode ? "rgba(49,178,139,.105)" : "rgba(71,193,151,.07)")
                                  : ui.surfaceBgSoft,
                                border: `1px solid ${isEquipped ? (isDarkMode ? "rgba(99,220,178,.40)" : "rgba(38,190,134,.38)") : ui.border}`,
                                boxShadow: isEquipped
                                  ? `inset 3px 0 0 ${isDarkMode ? "rgba(99,220,178,.72)" : "rgba(38,190,134,.62)"}`
                                  : "none",
                              }}
                            >
                              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px] text-[17px]" style={{ background: rarityTone.bg, color: rarityTone.fg, border: `1px solid ${ui.border}` }}>
                                {item.emoji}
                              </span>
                              <div className="min-w-0 flex-1">
                                <div className="truncate text-[11px] font-black" style={sectionTitleStyle}>{item.name}</div>
                                <div className="mt-0.5 truncate text-[8px] font-bold uppercase tracking-[0.09em]" style={subtleTextStyle}>
                                  {getCosmeticSlotLabel(item.slot as CosmeticSlot)} · {item.rarity}
                                </div>
                              </div>
                              <span
                                className="shrink-0 rounded-[9px] px-2 py-1.5 text-[9px] font-black"
                                style={{
                                  background: isEquipped ? theme.rewardMintSoft : theme.rewardBlueSoft,
                                  color: isEquipped ? theme.rewardMintText : theme.rewardBlueText,
                                  border: `1px solid ${ui.border}`,
                                }}
                              >
                                {isBusy ? "Saving…" : isEquipped ? "✓ On" : "Equip"}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="rounded-[14px] border px-3 py-4 text-center text-[10px] font-bold" style={{ borderColor: ui.border, color: ui.textSecondary }}>
                        No owned items in this category yet.
                      </div>
                    )}
                  </div>

                  <div className="mt-4 border-t pt-4" style={{ borderColor: ui.border }}>
                    <div className="mb-2 flex items-end justify-between gap-3">
                      <div>
                        <div className="text-[10px] font-black uppercase tracking-[0.14em]" style={{ color: theme.rewardGoldText }}>
                          Shell Shop
                        </div>
                        <div className="mt-0.5 text-[11px] font-bold" style={sectionBodyStyle}>
                          Unlock with progress. Spend Shells on the look.
                        </div>
                      </div>
                      <span className="shrink-0 rounded-full px-2.5 py-1 text-[9px] font-black" style={{ background: theme.rewardGoldSoft, color: theme.rewardGoldText, border: `1px solid ${ui.border}` }}>
                        {shellBalance.toLocaleString()} 🐚
                      </span>
                    </div>

                    {filteredShopCosmetics.length ? (
                      <div className="grid gap-2 min-[520px]:grid-cols-2">
                        {filteredShopCosmetics.map((item) => {
                          const unlockLevel = Math.max(1, Number(item.unlockLevel || 1));
                          const isLevelLocked = anglerLevel < unlockLevel;
                          const canAfford = shellBalance >= item.costShells;
                          const isBusy = purchasingCosmeticId === item.id;
                          const rarity = String(item.rarity || "common").toLowerCase();
                          const rarityTone = rarity === "legendary"
                            ? { bg: theme.rewardGoldSoft, fg: theme.rewardGoldText }
                            : rarity === "epic"
                              ? { bg: isDarkMode ? "rgba(146,120,236,0.14)" : "rgba(112,87,232,0.10)", fg: isDarkMode ? "#D9D0FF" : "#5E49B8" }
                              : rarity === "rare"
                                ? { bg: isDarkMode ? "rgba(103,216,232,0.12)" : "rgba(47,128,237,0.10)", fg: isDarkMode ? "#A9EEF5" : "#245F91" }
                                : { bg: theme.rewardMintSoft, fg: theme.rewardMintText };

                          return (
                            <div
                              key={`shop-${item.id}`}
                              className="rounded-[12px] p-2.5"
                              style={{
                                background: isLevelLocked
                                  ? (isDarkMode ? "rgba(12,20,35,.72)" : "rgba(250,247,241,.72)")
                                  : ui.surfaceBgSoft,
                                border: `1px solid ${ui.border}`,
                                opacity: isLevelLocked ? 0.78 : 1,
                              }}
                            >
                              <div className="flex items-start gap-2.5">
                                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[10px] text-[18px]" style={{ background: rarityTone.bg, color: rarityTone.fg, border: `1px solid ${ui.border}` }}>
                                  {item.emoji}
                                </span>
                                <div className="min-w-0 flex-1">
                                  <div className="flex min-w-0 items-center gap-1.5">
                                    <div className="truncate text-[11px] font-black" style={sectionTitleStyle}>{item.name}</div>
                                    <span className="shrink-0 rounded-[7px] px-1.5 py-0.5 text-[7px] font-black uppercase tracking-[0.10em]" style={{ background: rarityTone.bg, color: rarityTone.fg }}>
                                      {item.rarity}
                                    </span>
                                  </div>
                                  <div className="mt-0.5 text-[8px] font-bold uppercase tracking-[0.09em]" style={subtleTextStyle}>
                                    {getCosmeticSlotLabel(item.slot as CosmeticSlot)} · Lv {unlockLevel}
                                  </div>
                                  <p className="mt-1 line-clamp-2 text-[9px] font-semibold leading-4" style={sectionBodyStyle}>{item.description}</p>
                                </div>
                              </div>

                              <div className="mt-2 flex items-center justify-between gap-2 border-t pt-2" style={{ borderColor: ui.border }}>
                                <span className="text-[9px] font-bold" style={subtleTextStyle}>
                                  {isLevelLocked
                                    ? `Unlocks at level ${unlockLevel}`
                                    : canAfford
                                      ? "Ready"
                                      : `Need ${(item.costShells - shellBalance).toLocaleString()} more`}
                                </span>
                                <button
                                  type="button"
                                  disabled={isBusy || isLevelLocked || !canAfford}
                                  onClick={() => requestPurchaseAvatarCosmetic(item.id)}
                                  className="shrink-0 rounded-[9px] px-2.5 py-1.5 text-[9px] font-black disabled:cursor-not-allowed disabled:opacity-50"
                                  style={{
                                    background: isLevelLocked
                                      ? (isDarkMode ? "rgba(255,255,255,.035)" : "rgba(48,39,30,.035)")
                                      : theme.rewardGoldSoft,
                                    color: isLevelLocked ? ui.textSecondary : theme.rewardGoldText,
                                    border: `1px solid ${ui.border}`,
                                  }}
                                >
                                  {isBusy ? "Buying…" : isLevelLocked ? `Lv ${unlockLevel}` : `${item.costShells} 🐚`}
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="rounded-[14px] border px-3 py-4 text-center text-[10px] font-bold" style={{ borderColor: ui.border, color: ui.textSecondary }}>
                        Nothing else in this category right now.
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className={edgeLane}>
                <div
                  ref={searchLuresSectionRef}
                  className="roddo-polish-card rounded-[20px] p-3.5"
                  style={{ ...cardStyle, background: isDarkMode ? "#0C1725" : "#F8FCFE" }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[9px] font-black uppercase tracking-[0.16em]" style={{ color: theme.rewardBlueText }}>
                        Big Blue utility
                      </div>
                      <h3 className="mt-1 text-[16px] font-black" style={sectionTitleStyle}>Search Lures</h3>
                      <p className="mt-1 max-w-md text-[10px] font-bold leading-4" style={sectionBodyStyle}>
                        1 lure per new manual area. Today&apos;s Cast adds +5.
                      </p>
                    </div>
                    <span
                      className="shrink-0 rounded-[11px] px-3 py-2 text-[12px] font-black"
                      style={{ background: theme.rewardBlueSoft, color: theme.rewardBlueText, border: `1px solid ${ui.border}` }}
                    >
                      {searchLureBalance} 🪝
                    </span>
                  </div>

                  <div className="mt-3 flex items-center justify-between gap-3 rounded-[12px] border px-3 py-2" style={{ background: ui.surfaceBgSoft, borderColor: ui.border }}>
                    <span className="text-[9px] font-bold" style={{ color: ui.textSecondary }}>
                      {searchLureWallet.isAtOrAboveFreeCap
                        ? `Free refill paused at ${SEARCH_LURE_FREE_REGEN_CAP}`
                        : searchLureRefillLabel}
                    </span>
                    <span className="text-[8px] font-black uppercase tracking-[0.10em]" style={subtleTextStyle}>
                      Free cap {SEARCH_LURE_FREE_REGEN_CAP}
                    </span>
                  </div>

                  {rewardedSearchLureAvailable ? (
                    <button
                      type="button"
                      disabled={rewardedAdBusy || !!purchasingLureBundleId}
                      onClick={() => void handleRewardedSearchLure()}
                      className="mt-3 flex w-full items-center justify-between gap-3 rounded-[12px] border px-3 py-2.5 text-left disabled:cursor-not-allowed disabled:opacity-50"
                      style={{ background: theme.rewardMintSoft, borderColor: ui.border }}
                    >
                      <span>
                        <span className="block text-[10px] font-black" style={{ color: theme.rewardMintText }}>
                          {rewardedAdBusy
                            ? (RODDO_ADMOB.testMode ? "Loading test ad…" : "Loading ad…")
                            : (RODDO_ADMOB.testMode ? "Watch a test ad" : `Watch an ad · +${REWARDED_SEARCH_LURE_AMOUNT}`)}
                        </span>
                        <span className="mt-0.5 block text-[8px] font-bold" style={{ color: ui.textSecondary }}>
                          {RODDO_ADMOB.testMode
                            ? "QA only · test ads never grant Search Lures"
                            : `Earn ${REWARDED_SEARCH_LURE_AMOUNT} Search Lures · up to ${REWARDED_SEARCH_LURE_DAILY_CAP} ads/day`}
                        </span>
                      </span>
                      <span className="text-[16px]" aria-hidden="true">▶️</span>
                    </button>
                  ) : null}

                  <div className="mt-3 grid grid-cols-3 gap-2">
                    {SEARCH_LURE_BUNDLES.map((bundle, index) => {
                      const busy = purchasingLureBundleId === bundle.id;
                      const canAfford = shellBalance >= bundle.costShells;
                      const bestValue = index === SEARCH_LURE_BUNDLES.length - 1;
                      return (
                        <button
                          key={bundle.id}
                          type="button"
                          disabled={!!purchasingLureBundleId || !canAfford}
                          onClick={() => requestPurchaseSearchLures(bundle.id)}
                          className="relative min-w-0 rounded-[12px] px-2.5 py-2.5 text-left disabled:cursor-not-allowed disabled:opacity-50"
                          style={{ background: ui.surfaceBgSoft, border: `1px solid ${bestValue ? (isDarkMode ? "rgba(103,216,232,.30)" : "rgba(47,128,237,.22)") : ui.border}` }}
                          title={!canAfford ? `Need ${bundle.costShells - shellBalance} more Shells` : `Buy ${bundle.quantity} Search Lures`}
                        >
                          {bestValue ? (
                            <span className="absolute right-2 top-2 rounded-[7px] px-1.5 py-0.5 text-[7px] font-black uppercase tracking-[0.08em]" style={{ background: theme.rewardMintSoft, color: theme.rewardMintText }}>
                              Value
                            </span>
                          ) : null}
                          <div className="text-[9px] font-black" style={subtleTextStyle}>{bundle.name.replace(" Pack", "")}</div>
                          <div className="mt-1 text-[18px] font-black leading-none" style={{ color: theme.rewardBlueText }}>+{bundle.quantity}</div>
                          <div className="mt-2 text-[9px] font-black" style={{ color: canAfford ? theme.rewardGoldText : ui.textSecondary }}>
                            {busy ? "Buying…" : `${bundle.costShells} 🐚`}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {activeTab === "tackle" ? (
            <div className="space-y-5 pb-4">
              <div className={edgeLane}>
                <div
                  className="roddo-polish-energy roddo-polish-card relative overflow-hidden rounded-[26px] p-4"
                  style={cardStyle}
                >
                  <div
                    className="pointer-events-none absolute inset-x-0 top-0 h-1"
                    style={{
                      background: isDarkMode
                        ? "linear-gradient(90deg, rgba(112,87,232,0), #8A70F2, rgba(112,87,232,0))"
                        : "linear-gradient(90deg, rgba(47,128,237,0), #2F80ED, rgba(47,128,237,0))",
                    }}
                  />

                  <div className="relative flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div
                        className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[11px] font-black uppercase tracking-[0.16em]"
                        style={{
                          background: theme.rewardBlueSoft,
                          color: theme.rewardBlueText,
                          border: `1px solid ${ui.border}`,
                        }}
                      >
                        <span aria-hidden="true">▣</span>
                        <span>License passport</span>
                      </div>
                      <h2
                        className="mt-2 text-lg font-black leading-tight"
                        style={sectionTitleStyle}
                      >
                        {activeCount > 0 ? "Ready to fish" : "License locker"}
                      </h2>
                      <p
                        className="mt-1 text-[11px] font-bold leading-4"
                        style={sectionBodyStyle}
                      >
                        {activeCount > 0
                          ? isExpiringSoon
                            ? "License proof is ready. Renewal is coming up."
                            : "License proof is ready when you need it."
                          : t(
                              "tackleBox.licenses.subtitle",
                              "Keep your fishing licenses within reach.",
                            )}
                      </p>
                    </div>

                    <div className="flex shrink-0 items-center gap-2">
                      {activeCount > 0 && (
                        <span
                          className="hidden rounded-full px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.12em] min-[380px]:inline-flex"
                          style={{
                            background: theme.rewardMintSoft,
                            color: theme.rewardMintText,
                            border: `1px solid ${ui.border}`,
                          }}
                        >
                          {activeCount} active on file
                        </span>
                      )}
                      <button
                        ref={licenseMenuBtnRef}
                        type="button"
                        onClick={() =>
                          licenseMenuOpen
                            ? setLicenseMenuOpen(false)
                            : openOverflowMenu(
                                licenseMenuBtnRef.current,
                                setLicenseMenuPos,
                                setLicenseMenuOpen,
                              )
                        }
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition"
                        style={{
                          color: ui.textSecondary,
                          background: ui.surfaceBgSoft,
                          border: `1px solid ${ui.border}`,
                        }}
                        aria-label={t("common.more", "More") as string}
                      >
                        <span className="text-lg font-black leading-none">
                          …
                        </span>
                      </button>
                    </div>
                  </div>

                  <div className="relative mt-4 grid gap-3 sm:grid-cols-[1fr_132px]">
                    <div
                      className="rounded-[20px] p-3"
                      style={mutedCardStyle}
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className="inline-flex items-center rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em]"
                          style={{
                            background:
                              activeCount > 0
                                ? theme.rewardMintSoft
                                : isExpired
                                  ? "rgba(255,159,176,0.18)"
                                  : theme.rewardGoldSoft,
                            color:
                              activeCount > 0
                                ? theme.rewardMintText
                                : isExpired
                                  ? "#a61f3d"
                                  : theme.rewardGoldText,
                          }}
                        >
                          {activeCount > 0
                            ? "Active"
                            : isExpired
                              ? "Expired"
                              : "Needs attention"}
                        </span>
                        {licenseType ? (
                          <span
                            className="inline-flex min-w-0 max-w-full items-center truncate rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-[0.16em]"
                            style={{
                              background: theme.rewardBlueSoft,
                              color: theme.rewardBlueText,
                            }}
                          >
                            {licenseType}
                          </span>
                        ) : null}
                      </div>

                      <div className="mt-4 grid grid-cols-2 gap-2">
                        <div
                          className="rounded-[14px] px-3 py-2"
                          style={{
                            background: ui.surfaceBgSoft,
                            border: `1px solid ${ui.border}`,
                          }}
                        >
                          <div
                            className="text-[9px] font-black uppercase tracking-[0.18em]"
                            style={subtleTextStyle}
                          >
                            State
                          </div>
                          <div
                            className="mt-1 truncate text-sm font-black"
                            style={sectionTitleStyle}
                          >
                            {licenseState || selectedLicenseRegionName || selectedLicenseCountryName}
                          </div>
                        </div>
                        <div
                          className="rounded-[14px] px-3 py-2"
                          style={{
                            background: ui.surfaceBgSoft,
                            border: `1px solid ${ui.border}`,
                          }}
                        >
                          <div
                            className="text-[9px] font-black uppercase tracking-[0.18em]"
                            style={subtleTextStyle}
                          >
                            Expires
                          </div>
                          <div
                            className="mt-1 truncate text-sm font-black"
                            style={sectionTitleStyle}
                          >
                            {formatDateShort(licenseExpiry)}
                          </div>
                        </div>
                      </div>

                      <p
                        className="mt-3 text-[11px] font-bold leading-5"
                        style={sectionBodyStyle}
                      >
                        {activeCount > 0
                          ? isExpiringSoon
                            ? "Renewal is getting close. Keep your proof handy before your next trip."
                            : "Your active license is ready whenever you need it."
                          : "No active license found. Use the official shortcut below to buy or renew fast."}
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() =>
                        licenseThumbUrl && setLightboxUrl(licenseThumbUrl)
                      }
                      disabled={!licenseThumbUrl}
                      className="relative h-[132px] overflow-hidden rounded-[20px]"
                      style={{
                        ...mutedCardStyle,
                        cursor: licenseThumbUrl ? "zoom-in" : "default",
                        opacity: licenseThumbUrl ? 1 : 0.96,
                      }}
                    >
                      {licenseThumbUrl ? (
                        <>
                          <img
                            src={licenseThumbUrl}
                            alt="License"
                            className="h-full w-full object-cover"
                          />
                          <div className="pointer-events-none absolute inset-x-0 bottom-2 flex justify-center px-2">
                            <span className="inline-flex whitespace-nowrap rounded-full bg-white/92 px-2.5 py-1 text-[8px] font-black uppercase leading-none tracking-[0.10em] text-slate-800 shadow-[0_2px_8px_rgba(0,0,0,0.16)]">
                              Tap to view
                            </span>
                          </div>
                        </>
                      ) : (
                        <div className="flex h-full w-full items-center justify-center">
                          <div
                            className="rounded-[14px] p-3"
                            style={{
                              background: ui.surfaceBgSoft,
                              border: `1px solid ${ui.border}`,
                            }}
                          >
                            <IdCardIcon
                              className="h-12 w-12"
                              style={{ color: ui.textSecondary }}
                            />
                          </div>
                        </div>
                      )}
                    </button>
                  </div>

                  {showOfficialLink ? (
                    <div
                      className="relative mt-4 rounded-[18px] p-3"
                      style={mutedCardStyle}
                    >
                      <div className="flex flex-col gap-2 sm:flex-row">
                        <button
                          type="button"
                          onClick={openOfficialLicenseSite}
                          className="flex-1 rounded-full px-4 py-2 text-xs font-black"
                          style={{
                            background: "#2F80ED",
                            color: "#ffffff",
                            boxShadow: "0 12px 28px rgba(43,108,255,0.28)",
                          }}
                        >
                          Buy / Renew official
                        </button>
                        <button
                          type="button"
                          onClick={() => setLicenseJurisdictionPickerOpen(true)}
                          className="flex min-w-[112px] items-center justify-center gap-2 rounded-full px-3 py-2 text-xs font-extrabold transition active:scale-[0.98]"
                          style={{
                            background: ui.inputBg,
                            color: ui.textPrimary,
                            border: `1px solid ${ui.borderStrong}`,
                            boxShadow: ui.insetHighlight,
                          }}
                          aria-label={`Select fishing license jurisdiction. Current jurisdiction ${licenseJurisdictionLabel}`}
                          aria-haspopup="dialog"
                          aria-expanded={licenseJurisdictionPickerOpen}
                          title={licenseJurisdictionLabel}
                        >
                          <span className="max-w-[92px] truncate">
                            {licenseRegionCode || licenseCountryCode}
                          </span>
                          <span className="text-[11px] opacity-70" aria-hidden="true">⌄</span>
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>

              <div className={edgeLane}>
                <div
                  className="roddo-polish-energy roddo-polish-card relative overflow-hidden rounded-[26px] p-4"
                  style={cardStyle}
                >
                  <div className="relative flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div
                        className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[11px] font-black uppercase tracking-[0.18em]"
                        style={{
                          background: theme.rewardMintSoft,
                          color: theme.rewardMintText,
                          border: `1px solid ${ui.border}`,
                        }}
                      >
                        <span>{t("tackleBox.gear.title", "Equipped")}</span>
                      </div>
                      <h2
                        className="mt-2 text-lg font-black leading-tight"
                        style={sectionTitleStyle}
                      >
                        Quick log kit
                      </h2>
                      <p
                        className="mt-1 text-[11px] font-bold leading-4"
                        style={sectionBodyStyle}
                      >
                        Saved tackle fills catch logs faster.
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <div
                        className="rounded-[14px] px-3 py-2 text-center"
                        style={{
                          background: ui.surfaceBgSoft,
                          border: `1px solid ${ui.border}`,
                        }}
                      >
                        <div
                          className="text-[9px] font-black uppercase tracking-[0.15em]"
                          style={subtleTextStyle}
                        >
                          Equipped
                        </div>
                        <div
                          className="mt-0.5 text-lg font-black leading-none"
                          style={{ color: ui.textPrimary }}
                        >
                          {loadoutSlotsEquipped}/3
                        </div>
                      </div>
                      <button
                        ref={gearMenuBtnRef}
                        type="button"
                        onClick={() =>
                          gearMenuOpen
                            ? setGearMenuOpen(false)
                            : openOverflowMenu(
                                gearMenuBtnRef.current,
                                setGearMenuPos,
                                setGearMenuOpen,
                              )
                        }
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition"
                        style={{
                          color: ui.textSecondary,
                          background: ui.surfaceBgSoft,
                          border: `1px solid ${ui.border}`,
                        }}
                        aria-label={t("common.more", "More") as string}
                      >
                        <span className="text-lg font-black leading-none">
                          …
                        </span>
                      </button>
                    </div>
                  </div>

                  <div className="relative mt-4 space-y-3">
                    {renderEquipTile(
                      "Rod",
                      equippedRod,
                      "rod",
                      rods,
                      localEquipped.rodId,
                      "rodId",
                      theme.rewardBlueSoft,
                      theme.rewardBlueText,
                    )}
                    {renderEquipTile(
                      "Reel",
                      equippedReel,
                      "reel",
                      reels,
                      localEquipped.reelId,
                      "reelId",
                      theme.rewardMintSoft,
                      theme.rewardMintText,
                    )}
                    {renderEquipTile(
                      "Lure / bait",
                      equippedLureBait,
                      "lureOrBait",
                      luresBaits,
                      localEquipped.lureOrBaitId,
                      "lureOrBaitId",
                      theme.rewardGoldSoft,
                      theme.rewardGoldText,
                    )}
                  </div>

                  <div
                    className="mt-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 rounded-[12px] px-3 py-2 text-[10px] font-black uppercase tracking-[0.12em]"
                    style={{
                      background: isDarkMode ? "#0C1423" : ui.surfaceBgSoft,
                      color: ui.textSecondary,
                      border: "none",
                    }}
                  >
                    <span><strong style={{ color: theme.rewardBlueText }}>{rods.length}</strong> rods</span>
                    <span aria-hidden="true" style={{ color: ui.textMuted }}>·</span>
                    <span><strong style={{ color: theme.rewardMintText }}>{reels.length}</strong> reels</span>
                    <span aria-hidden="true" style={{ color: ui.textMuted }}>·</span>
                    <span><strong style={{ color: theme.rewardGoldText }}>{luresBaits.length}</strong> lures / baits</span>
                  </div>
                </div>
              </div>

              <div className={edgeLane}>
                <div
                  className="rounded-[18px] p-3"
                  style={{
                    background: isDarkMode ? "#0C1423" : ui.cardBgMuted,
                    border: `1px solid ${isDarkMode ? "rgba(132,151,190,0.10)" : ui.border}`,
                    boxShadow: "none",
                  }}
                >
                  <div className="mb-2 text-[9px] font-black uppercase tracking-[0.18em]" style={subtleTextStyle}>
                    Coming next
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <button
                      type="button"
                      onClick={() => safeToast("Smart device support is coming soon. 👀", "info")}
                      className="flex w-full items-center gap-3 rounded-[14px] px-3 py-2.5 text-left"
                      style={{ background: "transparent", border: "none" }}
                    >
                      <div
                        className="grid h-10 w-10 shrink-0 place-items-center rounded-[12px] text-base"
                        style={{ background: theme.rewardBlueSoft, color: theme.rewardBlueText }}
                      >
                        🔌
                      </div>
                      <div className="min-w-0">
                        <div className="text-[12px] font-black" style={sectionTitleStyle}>Smart devices</div>
                        <div className="mt-0.5 text-[10px] font-bold leading-4" style={sectionBodyStyle}>
                          Rods, reels, sensors, and trip gadgets.
                        </div>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => safeToast("SOS / Safety is coming soon. 👀", "info")}
                      className="flex w-full items-center gap-3 rounded-[14px] px-3 py-2.5 text-left"
                      style={{ background: "transparent", border: "none" }}
                    >
                      <div
                        className="grid h-10 w-10 shrink-0 place-items-center rounded-[12px]"
                        style={{ background: theme.rewardGoldSoft, color: theme.rewardGoldText }}
                      >
                        <AlertTriangleIcon className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="text-[12px] font-black" style={sectionTitleStyle}>
                            {t("tackleBox.sos.title", "SOS / Safety")}
                          </div>
                          <span
                            className="rounded-full px-2 py-0.5 text-[8px] font-black uppercase tracking-[0.12em]"
                            style={{ background: theme.rewardGoldSoft, color: theme.rewardGoldText }}
                          >
                            Soon
                          </span>
                        </div>
                        <div className="mt-0.5 text-[10px] font-bold leading-4" style={sectionBodyStyle}>
                          Emergency contacts and quick actions.
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {activeTab === "stats" ? (
            <div className="space-y-5 pb-6 pt-1">
              <div className={edgeLane}>
                <div
                  className="roddo-polish-energy roddo-polish-card relative overflow-hidden rounded-[26px] p-4"
                  style={cardStyle}
                >

                  <div className="relative space-y-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div
                          className="inline-flex rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-[0.22em]"
                          style={{
                            background: theme.rewardMintSoft,
                            color: theme.rewardMintText,
                          }}
                        >
                          Field summary
                        </div>
                        <h2
                          className="mt-3 text-[18px] font-black"
                          style={sectionTitleStyle}
                        >
                          The Hold scorecard
                        </h2>
                        <p
                          className="mt-1 max-w-[28rem] text-[11px] leading-5"
                          style={sectionBodyStyle}
                        >
                          One clean read for level, XP, catches, species, and
                          saved kit.
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          if (onOpenCatchHistory) onOpenCatchHistory();
                          else
                            safeToast(
                              "Catch history is not wired yet.",
                              "error",
                            );
                        }}
                        className="shrink-0 rounded-[15px] px-3 py-2 text-[10px] font-black uppercase tracking-[0.14em] transition-transform hover:-translate-y-0.5 active:translate-y-0"
                        style={{
                          background: isDarkMode ? "#67D8E8" : "#2F80ED",
                          color: "#FFFFFF",
                          boxShadow: isDarkMode ? "0 8px 18px rgba(36,25,96,0.22)" : "0 8px 18px rgba(47,128,237,0.16)",
                        }}
                      >
                        Open log
                      </button>
                    </div>

                    <div
                      className="rounded-[1.7rem] p-4"
                      style={mutedCardStyle}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div
                            className="text-[10px] font-black uppercase tracking-[0.18em]"
                            style={subtleTextStyle}
                          >
                            Level
                          </div>
                          <div className="mt-1 flex flex-wrap items-end gap-2">
                            <span
                              className="text-4xl font-black leading-none"
                              style={{ color: ui.textPrimary }}
                            >
                              {anglerLevel}
                            </span>
                            <span
                              className="mb-1 rounded-full px-2.5 py-1 text-[10px] font-black"
                              style={{
                                background: theme.rewardMint,
                                color: theme.rewardMintText,
                              }}
                            >
                              {xpInfo.rank}
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div
                            className="text-[10px] font-black uppercase tracking-[0.18em]"
                            style={subtleTextStyle}
                          >
                            Lifetime XP
                          </div>
                          <div
                            className="mt-1 text-2xl font-black leading-none"
                            style={{ color: ui.textPrimary }}
                          >
                            {xpInfo.totalXP.toLocaleString()}
                          </div>
                        </div>
                      </div>

                      <div
                        className="mt-4 flex items-center justify-between gap-3 text-[11px] font-extrabold"
                        style={sectionBodyStyle}
                      >
                        <span>
                          {Math.round(xpInfo.percent)}% through Level{" "}
                          {anglerLevel}
                        </span>
                        <span>
                          {xpInfo.gained.toLocaleString()} /{" "}
                          {xpInfo.needed.toLocaleString()} XP
                        </span>
                      </div>
                      <div
                        className="relative mt-3 h-4 overflow-hidden rounded-full"
                        style={{
                          background: ui.surfaceBgSoft || ui.cardBg,
                          border: `1px solid ${ui.border}`,
                        }}
                      >
                        <div
                          className="absolute inset-y-0 left-0 rounded-full"
                          style={{
                            width: `${Math.max(0, Math.min(100, xpInfo.percent || 0))}%`,
                            background: isDarkMode ? "#67D8E8" : "#2F80ED",
                            boxShadow: "none",
                          }}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
                      {[
                        {
                          icon: "🌊",
                          label: "Next level",
                          value: xpInfo.remaining.toLocaleString(),
                          color: theme.rewardBlueText,
                        },
                        {
                          icon: "🎣",
                          label: "Catches",
                          value: totalCatches.toLocaleString(),
                          color: theme.rewardBlueText,
                        },
                        {
                          icon: "🐟",
                          label: "Species",
                          value: uniqueSpeciesCount || "—",
                          color: theme.rewardMintText,
                        },
                        {
                          icon: "🧰",
                          label: t('tackleBox.avatar.equipped', 'Equipped') as string,
                          value: `${loadoutSlotsEquipped}/3`,
                          color: theme.rewardGoldText,
                        },
                      ].map((item) => (
                        <div
                          key={item.label}
                          className="rounded-[16px] px-3 py-3"
                          style={{
                            background: ui.surfaceBgSoft || ui.cardBg,
                            border: `1px solid ${ui.border}`,
                          }}
                        >
                          <div
                            className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-[0.14em]"
                            style={subtleTextStyle}
                          >
                            <span>{item.icon}</span>
                            <span>{item.label}</span>
                          </div>
                          <div
                            className="mt-1 text-xl font-black leading-none"
                            style={{ color: item.color }}
                          >
                            {item.value}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className={edgeLane}>
                <div
                  className="roddo-polish-energy roddo-polish-card rounded-[26px] p-4"
                  style={cardStyle}
                >
                  <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div
                        className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[11px] font-black uppercase tracking-[0.18em]"
                        style={{
                          background: theme.rewardGoldSoft,
                          color: theme.rewardGoldText,
                        }}
                      >
                        <span>🧰</span>
                        <span>{t('tackleBox.deck.label', 'Equipment deck')}</span>
                      </div>
                      <h2
                        className="mt-2 text-[15px] font-extrabold leading-tight"
                        style={sectionTitleStyle}
                      >
                        {t('tackleBox.deck.title', 'Active kit + inventory')}
                      </h2>
                      <p
                        className="mt-0.5 text-[11px]"
                        style={sectionBodyStyle}
                      >
                        {t('tackleBox.deck.copy', 'Saved tackle up top. Equipped loadout below.')}
                      </p>
                    </div>
                    <div
                      className="rounded-[14px] px-3 py-2 text-right"
                      style={mutedCardStyle}
                    >
                      <div
                        className="text-[9px] font-black uppercase tracking-[0.16em]"
                        style={subtleTextStyle}
                      >
                        {t('tackleBox.avatar.equipped', 'Equipped')}
                      </div>
                      <div
                        className="mt-0.5 text-lg font-black leading-none"
                        style={{ color: ui.textPrimary }}
                      >
                        {loadoutSlotsEquipped}/3
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-2 sm:grid-cols-3">
                    {statMiniCards.map((card) => {
                      const icon =
                        card.label === "Rods"
                          ? "🎣"
                          : card.label === "Reels"
                            ? "🧵"
                            : "🪱";
                      return (
                        <div
                          key={card.label}
                          className="rounded-[16px] px-3 py-3"
                          style={mutedCardStyle}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div
                              className="grid h-8 w-8 place-items-center rounded-full text-sm"
                              style={{
                                background: card.accent,
                                color: card.text,
                                border: `1px solid ${ui.border}`,
                              }}
                            >
                              {icon}
                            </div>
                            <div className="min-w-0 text-right">
                              <div
                                className="truncate text-[9px] font-black uppercase tracking-[0.14em]"
                                style={{ color: card.text }}
                              >
                                {card.label}
                              </div>
                              <div
                                className="mt-0.5 text-2xl font-black leading-none"
                                style={{ color: ui.textPrimary }}
                              >
                                {card.value}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-2 grid gap-2 sm:grid-cols-3">
                    {[
                      {
                        icon: "🎣",
                        label: t('tackleBox.deck.activeRod', 'Active rod') as string,
                        value: equippedRod?.name || (t('tackleBox.gear.none', 'None equipped') as string),
                      },
                      {
                        icon: "🧵",
                        label: t('tackleBox.deck.activeReel', 'Active reel') as string,
                        value: equippedReel?.name || (t('tackleBox.gear.none', 'None equipped') as string),
                      },
                      {
                        icon: "🪱",
                        label: t('tackleBox.deck.activeLure', 'Active lure') as string,
                        value: equippedLureBait?.name || (t('tackleBox.gear.none', 'None equipped') as string),
                      },
                    ].map((item) => (
                      <div
                        key={item.label}
                        className="rounded-[14px] px-3 py-3"
                        style={mutedCardStyle}
                      >
                        <div
                          className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-[0.14em]"
                          style={subtleTextStyle}
                        >
                          <span>{item.icon}</span>
                          <span>{item.label}</span>
                        </div>
                        <div
                          className="mt-1.5 truncate text-[13px] font-black"
                          style={{ color: ui.textPrimary }}
                        >
                          {item.value}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className={edgeLane}>
                <div
                  className="roddo-polish-energy roddo-polish-card overflow-hidden rounded-[26px] p-4"
                  style={cardStyle}
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div
                        className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[11px] font-black uppercase tracking-[0.18em]"
                        style={{
                          background: theme.rewardMintSoft,
                          color: theme.rewardMintText,
                        }}
                      >
                        <span>FishIQ</span>
                      </div>
                      <h2
                        className="mt-2 text-[15px] font-extrabold leading-tight"
                        style={sectionTitleStyle}
                      >
                        Personal pattern read
                      </h2>
                      <p
                        className="mt-0.5 max-w-[30rem] text-[11px] leading-5"
                        style={sectionBodyStyle}
                      >
                        Your log patterns stay here. Area reads stay inside
                        FishIQ.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={openFishIQ}
                      className="rounded-[14px] px-3.5 py-2 text-[11px] font-extrabold uppercase tracking-[0.14em] transition-transform hover:-translate-y-0.5 active:translate-y-0"
                      style={{
                        background: isDarkMode ? "#67D8E8" : "#2F80ED",
                        color: "#FFFFFF",
                        boxShadow: isDarkMode
                          ? "0 8px 18px rgba(36,25,96,0.22)"
                          : "0 8px 18px rgba(47,128,237,0.16)",
                      }}
                    >
                      View FishIQ
                    </button>
                  </div>

                  <div className="mt-3 grid gap-2 sm:grid-cols-4">
                    {[
                      { icon: "✨", label: "Signal", value: fishIQSignalLabel },
                      { icon: "🐟", label: "Species", value: topSpeciesLabel },
                      { icon: "💧", label: "Water", value: topWaterLabel },
                      { icon: "🪱", label: "Lure", value: topLureLabel },
                    ].map((card) => (
                      <div
                        key={card.label}
                        className="rounded-[14px] px-3 py-3"
                        style={mutedCardStyle}
                      >
                        <div
                          className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-[0.14em]"
                          style={subtleTextStyle}
                        >
                          <span>{card.icon}</span>
                          <span>{card.label}</span>
                        </div>
                        <div
                          className="mt-1.5 min-h-[2.1rem] text-[12px] font-black leading-5"
                          style={{ color: ui.textPrimary }}
                        >
                          {card.value}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <span
                      className="rounded-full px-2.5 py-1 text-[11px] font-extrabold"
                      style={{
                        background: ui.surfaceBgSoft || ui.cardBg,
                        color: ui.textPrimary,
                        border: `1px solid ${ui.border}`,
                      }}
                    >
                      📚 {fishIQSampleLabel}
                    </span>
                    {fishIQSummary.windows[0]?.label ? (
                      <span
                        className="rounded-full px-2.5 py-1 text-[11px] font-extrabold"
                        style={{
                          background: ui.surfaceBgSoft || ui.cardBg,
                          color: ui.textPrimary,
                          border: `1px solid ${ui.border}`,
                        }}
                      >
                        🌅 {topWindowLabel}
                      </span>
                    ) : null}
                    {[
                      ...fishIQSummary.species.slice(0, 2),
                      ...fishIQSummary.waters.slice(0, 1),
                      ...fishIQSummary.lures.slice(0, 1),
                    ].map((item: any) => (
                      <span
                        key={`${item.label}-${item.count}`}
                        className="rounded-full px-2.5 py-1 text-[11px] font-extrabold"
                        style={{
                          background: ui.surfaceBgSoft || ui.cardBg,
                          color: ui.textPrimary,
                          border: `1px solid ${ui.border}`,
                        }}
                      >
                        {item.label} · {item.count}
                      </span>
                    ))}
                    {!fishIQSummary.sampleSize ? (
                      <p
                        className="basis-full text-[11px]"
                        style={sectionBodyStyle}
                      >
                        Open Log holds the full diary; FishIQ will get cleaner
                        as catch rows include time, water, and lure details.
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </RoddoPageShell>

      {typeof document !== "undefined" && licenseJurisdictionPickerOpen && portalTarget
        ? createPortal(
            <div
              className="fixed left-0 right-0 z-[100000] flex min-h-0 items-end justify-center bg-black/45 p-3 backdrop-blur-[3px] sm:items-center"
              style={{ top: "calc(var(--roddo-header-h, 96px) + 8px)", bottom: "calc(var(--roddo-nav-h, 84px) + 8px)" }}
              onMouseDown={(event) => {
                if (event.currentTarget === event.target) setLicenseJurisdictionPickerOpen(false);
              }}
              role="dialog"
              aria-modal="true"
              aria-label="Choose fishing license jurisdiction"
            >
              <div
                className="flex max-h-full w-full max-w-[430px] flex-col overflow-hidden rounded-[20px] border"
                style={{
                  background: isDarkMode ? "#121A2B" : "#FFFCF7",
                  borderColor: isDarkMode ? "rgba(180,195,220,0.20)" : "rgba(74,58,42,0.16)",
                  boxShadow: isDarkMode
                    ? "0 24px 60px rgba(0,0,0,0.48)"
                    : "0 24px 60px rgba(89,65,37,0.22)",
                  color: ui.textPrimary,
                }}
                onMouseDown={(event) => event.stopPropagation()}
              >
                <div className="flex items-start justify-between gap-3 border-b px-4 py-4" style={{ borderColor: ui.border }}>
                  <div className="min-w-0">
                    <div className="text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: isDarkMode ? "#67D8E8" : "#2F80ED" }}>
                      Fishing jurisdiction
                    </div>
                    <h3 className="mt-1 text-lg font-black">Choose where you fish</h3>
                    <p className="mt-1 text-[11px] font-semibold leading-4" style={{ color: ui.textSecondary }}>
                      Roddo opens the verified authority when one is mapped, otherwise a government-focused official search for this jurisdiction.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setLicenseJurisdictionPickerOpen(false)}
                    className="grid h-10 w-10 shrink-0 place-items-center rounded-full border text-lg font-black"
                    style={{ background: ui.surfaceBgSoft, borderColor: ui.border, color: ui.textPrimary }}
                    aria-label="Close jurisdiction picker"
                  >
                    ×
                  </button>
                </div>

                <div className="min-h-0 overflow-y-auto overscroll-contain p-3" style={{ scrollbarGutter: "stable" }}>
                  <div className="rounded-[16px] border p-3" style={{ background: ui.surfaceBgSoft, borderColor: ui.border }}>
                    <div className="text-[9px] font-black uppercase tracking-[0.16em]" style={subtleTextStyle}>Country</div>
                    <input
                      value={licenseCountryQuery}
                      onChange={(event) => setLicenseCountryQuery(event.target.value)}
                      placeholder="Search country…"
                      className="roddo-mobile-input mt-2 w-full scroll-mb-28 rounded-[12px] px-3 py-2.5 text-sm font-bold outline-none"
                      style={{ background: ui.inputBg, color: ui.textPrimary, border: `1px solid ${ui.borderStrong}` }}
                      autoComplete="off"
                    />
                    <div className="mt-2 max-h-[240px] space-y-1 overflow-y-auto pr-1">
                      {filteredLicenseCountries.map((country) => {
                        const selected = licenseCountryCode === country.code;
                        return (
                          <button
                            key={country.code}
                            type="button"
                            onClick={() => {
                              setLicenseCountryCode(country.code);
                              setLicenseRegionCode("");
                              setLicenseCountryQuery("");
                            }}
                            className="flex min-h-[44px] w-full items-center gap-3 rounded-[12px] border px-3 py-2 text-left"
                            style={{
                              background: selected
                                ? isDarkMode ? "rgba(103,216,232,0.10)" : "rgba(47,128,237,0.09)"
                                : "transparent",
                              borderColor: selected
                                ? isDarkMode ? "rgba(103,216,232,0.32)" : "rgba(47,128,237,0.32)"
                                : "transparent",
                              color: ui.textPrimary,
                            }}
                          >
                            <span className="grid h-8 w-10 shrink-0 place-items-center rounded-[9px] border text-[10px] font-black" style={{
                              background: selected ? (isDarkMode ? "#67D8E8" : "#2F80ED") : ui.surfaceBg,
                              borderColor: selected ? (isDarkMode ? "#67D8E8" : "#2F80ED") : ui.border,
                              color: selected ? "#fff" : ui.textPrimary,
                            }}>
                              {country.code}
                            </span>
                            <span className="min-w-0 flex-1 truncate text-[12px] font-bold">{country.name}</span>
                            {selected ? <span style={{ color: isDarkMode ? "#67D8E8" : "#2F80ED" }}>✓</span> : null}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="mt-3 rounded-[16px] border p-3" style={{ background: ui.surfaceBgSoft, borderColor: ui.border }}>
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-[9px] font-black uppercase tracking-[0.16em]" style={subtleTextStyle}>Region / state / province</div>
                        <div className="mt-1 text-[12px] font-black" style={{ color: ui.textPrimary }}>
                          {licenseRegionOptions.length ? "Choose the local jurisdiction" : "Not required for this shortcut"}
                        </div>
                      </div>
                      <div className="rounded-full border px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.12em]" style={{ borderColor: ui.border, color: ui.textSecondary }}>
                        {licenseCountryCode}
                      </div>
                    </div>

                    {licenseRegionOptions.length ? (
                      <div className="mt-3 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                        {licenseRegionOptions.map((region) => {
                          const selected = licenseRegionCode === region.code;
                          return (
                            <button
                              key={region.code}
                              type="button"
                              onClick={() => setLicenseRegionCode(region.code)}
                              className="flex min-h-[46px] items-center gap-2 rounded-[12px] border px-3 py-2 text-left"
                              style={{
                                background: selected
                                  ? isDarkMode ? "rgba(103,216,232,0.10)" : "rgba(47,128,237,0.09)"
                                  : isDarkMode ? "#182238" : "#F7F0E6",
                                borderColor: selected
                                  ? isDarkMode ? "rgba(103,216,232,0.32)" : "rgba(47,128,237,0.32)"
                                  : ui.border,
                              }}
                            >
                              <span className="w-9 shrink-0 text-[10px] font-black" style={{ color: selected ? (isDarkMode ? "#67D8E8" : "#2F80ED") : ui.textSecondary }}>
                                {region.code}
                              </span>
                              <span className="min-w-0 flex-1 text-[11px] font-bold leading-4" style={{ color: ui.textPrimary }}>{region.name}</span>
                              {selected ? <span style={{ color: isDarkMode ? "#67D8E8" : "#2F80ED" }}>✓</span> : null}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="mt-2 text-[11px] font-semibold leading-5" style={{ color: ui.textSecondary }}>
                        Roddo will use {selectedLicenseCountryName} as the jurisdiction. Local waterbody permits and special-area rules may still apply.
                      </p>
                    )}
                  </div>

                  <div className="mt-3 rounded-[16px] border p-3" style={{ background: isDarkMode ? "rgba(103,216,232,0.07)" : "rgba(47,128,237,0.06)", borderColor: ui.border }}>
                    <div className="text-[9px] font-black uppercase tracking-[0.16em]" style={{ color: isDarkMode ? "#67D8E8" : "#2F80ED" }}>Current shortcut</div>
                    <div className="mt-1 text-sm font-black" style={{ color: ui.textPrimary }}>{licenseJurisdictionLabel}</div>
                    <p className="mt-1 text-[11px] font-semibold leading-4" style={{ color: ui.textSecondary }}>
                      Always verify current local seasons, permits, closures, species limits, and water-specific rules before fishing or harvesting.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setLicenseJurisdictionPickerOpen(false);
                      openOfficialLicenseSite();
                    }}
                    className="mt-3 w-full rounded-[14px] px-4 py-3 text-[11px] font-black uppercase tracking-[0.13em] text-white"
                    style={{ background: isDarkMode ? "#67D8E8" : "#2F80ED" }}
                  >
                    Open official authority
                  </button>
                </div>
              </div>
            </div>,
            portalTarget,
          )
        : null}

      {typeof document !== "undefined" && licenseMenuOpen && licenseMenuPos && portalTarget
        ? createPortal(
            <>
              <div
                className="fixed inset-0 z-[99998]"
                onClick={() => setLicenseMenuOpen(false)}
              />
              <div
                className="fixed z-[99999] w-[260px] overflow-hidden rounded-[22px]"
                style={{
                  ...menuStyle,
                  top: licenseMenuPos.top,
                  left: licenseMenuPos.left,
                }}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                role="dialog"
                aria-label="Licenses menu"
              >
                <div
                  className="border-b px-4 py-3"
                  style={{ borderColor: ui.border }}
                >
                  <div
                    className="text-[10px] font-black uppercase tracking-[0.22em]"
                    style={subtleTextStyle}
                  >
                    Licenses
                  </div>
                </div>
                <div className="p-2">
                  <button
                    type="button"
                    onClick={() => {
                      setLicenseMenuOpen(false);
                      go(
                        ((View as any).LicenseDetail ??
                          (View as any).License ??
                          View.MyPage) as View,
                      );
                    }}
                    className="flex w-full items-center gap-2 rounded-[1rem] px-3 py-2 text-[11px] font-semibold transition"
                    style={{ color: ui.textPrimary }}
                  >
                    <span>🪪</span>
                    <span>
                      {t("tackleBox.licenses.manage", "Manage licenses")}
                    </span>
                  </button>
                  <button
                    type="button"
                    disabled={!licenseThumbUrl}
                    onClick={() => {
                      setLicenseMenuOpen(false);
                      if (licenseThumbUrl) setLightboxUrl(licenseThumbUrl);
                    }}
                    className="mt-1 flex w-full items-center gap-2 rounded-[1rem] px-3 py-2 text-[11px] font-semibold transition"
                    style={{
                      color: licenseThumbUrl ? ui.textPrimary : ui.textMuted,
                      opacity: licenseThumbUrl ? 1 : 0.55,
                    }}
                  >
                    <span>🖼️</span>
                    <span>
                      {t("tackleBox.licenses.viewPhoto", "View photo")}
                    </span>
                  </button>
                </div>
              </div>
            </>,
            portalTarget,
          )
        : null}

      {typeof document !== "undefined" && gearMenuOpen && gearMenuPos && portalTarget
        ? createPortal(
            <>
              <div
                className="fixed inset-0 z-[99998]"
                onClick={() => setGearMenuOpen(false)}
              />
              <div
                className="fixed z-[99999] w-[260px] overflow-hidden rounded-[22px]"
                style={{
                  ...menuStyle,
                  top: gearMenuPos.top,
                  left: gearMenuPos.left,
                }}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                role="dialog"
                aria-label="Gear menu"
              >
                <div
                  className="border-b px-4 py-3"
                  style={{ borderColor: ui.border }}
                >
                  <div
                    className="text-[10px] font-black uppercase tracking-[0.22em]"
                    style={subtleTextStyle}
                  >
                    Equipped gear
                  </div>
                </div>
                <div className="p-2">
                  <button
                    type="button"
                    onClick={() => {
                      setGearMenuOpen(false);
                      go(View.MyPage);
                    }}
                    className="flex w-full items-center gap-2 rounded-[1rem] px-3 py-2 text-[11px] font-semibold"
                    style={{ color: ui.textPrimary }}
                  >
                    <span>⚙️</span>
                    <span>{t("tackleBox.gear.manage", "Manage all gear")}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setGearMenuOpen(false);
                      setIsAddGearOpen(true);
                    }}
                    className="mt-1 flex w-full items-center gap-2 rounded-[1rem] px-3 py-2 text-[11px] font-semibold"
                    style={{ color: ui.textPrimary }}
                  >
                    <span>➕</span>
                    <span>{t("tackleBox.gear.add", "Add gear")}</span>
                  </button>
                </div>
                <div
                  className="border-t p-2"
                  style={{ borderColor: ui.border }}
                >
                  <button
                    type="button"
                    onClick={clearEquipped}
                    className="flex w-full items-center gap-2 rounded-[1rem] px-3 py-2 text-[11px] font-semibold"
                    style={{ color: "#ff7b8f" }}
                  >
                    <span>🧹</span>
                    <span>
                      {t("tackleBox.gear.clear", "Clear equipped gear")}
                    </span>
                  </button>
                </div>
              </div>
            </>,
            portalTarget,
          )
        : null}

      {lightboxUrl && typeof document !== "undefined" && portalTarget
        ? createPortal(
            <div
              className="fixed inset-0 z-[2147483647] flex items-center justify-center bg-black/70 p-4"
              onClick={() => setLightboxUrl(null)}
              role="button"
              tabIndex={0}
            >
              <img
                src={lightboxUrl}
                alt="License photo"
                className="block max-h-[90vh] max-w-[95vw] rounded-2xl object-contain shadow-2xl"
                onClick={() => setLightboxUrl(null)}
              />
            </div>,
            portalTarget,
          )
        : null}

      <RoddoConfirmDialog
        open={!!pendingShellPurchase}
        eyebrow={pendingShellPurchase?.kind === "search_lures" ? "Search Lure refill" : "Shell Shop purchase"}
        title={
          pendingShellPurchase?.kind === "search_lures"
            ? `Buy ${pendingShellPurchase.quantity} Search Lures?`
            : pendingShellPurchase
              ? `Buy ${pendingShellPurchase.name}?`
              : "Confirm purchase"
        }
        description={
          pendingShellPurchase
            ? `Spend ${pendingShellPurchase.costShells.toLocaleString()} Shells? Your balance updates only after Roddo confirms the purchase.`
            : undefined
        }
        confirmLabel={
          pendingShellPurchase
            ? `Buy for ${pendingShellPurchase.costShells.toLocaleString()} 🐚`
            : "Confirm purchase"
        }
        cancelLabel="Not now"
        tone="info"
        icon={pendingShellPurchase?.kind === "search_lures" ? "🪝" : pendingShellPurchase?.emoji || "🛍️"}
        isBusy={!!purchasingCosmeticId || !!purchasingLureBundleId}
        isDarkMode={isDarkMode}
        closeOnBackdrop={false}
        onConfirm={confirmPendingShellPurchase}
        onCancel={() => {
          if (!purchasingCosmeticId && !purchasingLureBundleId) setPendingShellPurchase(null);
        }}
      >
        {pendingShellPurchase ? (
          <div
            className="rounded-[14px] border px-3 py-3"
            style={{
              background: ui.surfaceBgSoft,
              borderColor: ui.border,
            }}
          >
            <div className="grid grid-cols-2 gap-3 text-left">
              <div>
                <div className="text-[9px] font-black uppercase tracking-[0.16em]" style={subtleTextStyle}>Current wallet</div>
                <div className="mt-1 text-base font-black" style={{ color: ui.textPrimary }}>{shellBalance.toLocaleString()} 🐚</div>
              </div>
              <div>
                <div className="text-[9px] font-black uppercase tracking-[0.16em]" style={subtleTextStyle}>After purchase</div>
                <div className="mt-1 text-base font-black" style={{ color: theme.rewardGoldText }}>
                  {Math.max(0, shellBalance - pendingShellPurchase.costShells).toLocaleString()} 🐚
                </div>
              </div>
            </div>
            {pendingShellPurchase.kind === "search_lures" ? (
              <div className="mt-3 border-t pt-3" style={{ borderColor: ui.border }}>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[10px] font-bold" style={sectionBodyStyle}>Search Lures</span>
                  <span className="text-sm font-black" style={{ color: theme.rewardBlueText }}>
                    {searchLureBalance} → {searchLureBalance + pendingShellPurchase.quantity} 🪝
                  </span>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </RoddoConfirmDialog>

      <RoddoConfirmDialog
        open={!!purchaseSuccess}
        eyebrow="Purchase complete"
        title={purchaseSuccess?.title || "Purchase complete"}
        description={purchaseSuccess?.description}
        confirmLabel="Done"
        tone="success"
        icon={purchaseSuccess?.icon || "✓"}
        showCancel={false}
        closeOnBackdrop={false}
        isDarkMode={isDarkMode}
        onConfirm={() => setPurchaseSuccess(null)}
        onCancel={() => setPurchaseSuccess(null)}
      >
        {purchaseSuccess ? (
          <div
            className="rounded-[14px] border px-3 py-3"
            style={{
              background: ui.surfaceBgSoft,
              borderColor: ui.border,
            }}
          >
            <div className={`grid gap-3 text-left ${purchaseSuccess.searchLureBalance == null ? "grid-cols-1" : "grid-cols-2"}`}>
              <div>
                <div className="text-[9px] font-black uppercase tracking-[0.16em]" style={subtleTextStyle}>Shells remaining</div>
                <div className="mt-1 text-base font-black" style={{ color: theme.rewardGoldText }}>
                  {purchaseSuccess.shellBalance.toLocaleString()} 🐚
                </div>
              </div>
              {purchaseSuccess.searchLureBalance != null ? (
                <div>
                  <div className="text-[9px] font-black uppercase tracking-[0.16em]" style={subtleTextStyle}>Search Lures</div>
                  <div className="mt-1 text-base font-black" style={{ color: theme.rewardBlueText }}>
                    {purchaseSuccess.searchLureBalance.toLocaleString()} 🪝
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </RoddoConfirmDialog>

      <RoddoConfirmDialog
        open={isClearEquippedConfirmOpen}
        title={t("tackleBox.gear.clearConfirm", "Clear all equipped gear?") as string}
        description={t("tackleBox.gear.clearDescription", "This only clears your active loadout. Your saved gear stays in The Hold.") as string}
        confirmLabel={t("common.clear", "Clear") as string}
        cancelLabel={t("common.cancel", "Cancel") as string}
        tone="warning"
        isBusy={isClearingEquipped}
        isDarkMode={isDarkMode}
        onConfirm={confirmClearEquipped}
        onCancel={() => { if (!isClearingEquipped) setIsClearEquippedConfirmOpen(false); }}
      />

      <AddGearModal
        open={isAddGearOpen}
        onClose={() => setIsAddGearOpen(false)}
        onAdded={() => {
          setIsAddGearOpen(false);
          safeToast(
            t("tackleBox.addGear.success", "Added to the Hold."),
            "success",
          );
        }}
        userProfile={userProfile}
        addToast={addToast}
      />
    </>
  );
};

export default TackleBoxView;

